import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  BadgeDollarSign,
  BookOpen,
  FileText,
  GraduationCap,
  HeartPulse,
  Home,
  KeyRound,
  Languages,
  PencilLine,
  Phone,
  Plus,
  Save,
  Search,
  ShieldAlert,
  User,
  Users,
  X,
} from "lucide-react";

import { useAuth } from "../../../../auth/useAuth";
import ActionToast from "../../../../core/components/ActionToast";
import CampusInlineLoading from "../../components/CampusInlineLoading";
import {
  createCampusStudent,
  inviteCampusParentPortal,
  loadCampusStudents,
  searchCampusStudents,
  setCampusParentPortalAccess,
  uploadCampusStudentPhoto,
  updateCampusStudent,
} from "../../services/studentService";
import { loadCampusStaffDashboard } from "../../services/staffService";
import { loadCampusStudentAcademicRecords } from "../../services/teacherService";

const EDIT_SECTION_LABELS = {
  account: "Account Information",
  demographics: "Identity And Demographics",
  enrollment: "Enrollment And Tuition",
  tuition: "Tuition Billing",
  household: "Household And Contacts",
  guardians: "Guardians",
  emergency: "Emergency Contacts",
  medical: "Medical",
  disciplinary: "Disciplinary",
  parentContact: "Parent Contact Log",
  programs: "Programs And Extra Details",
};

function formatDate(value) {
  if (!value) return "Not set";

  try {
    return new Date(value).toLocaleDateString();
  } catch (_error) {
    return value;
  }
}

function formatCurrencyFromCents(value) {
  const cents = Number(value || 0) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function formatScore(value, pointsPossible) {
  if (value === "" || value === null || value === undefined) {
    return "Not graded";
  }

  const score = Number(value);
  const possible = Number(pointsPossible || 0);

  if (!Number.isFinite(score)) {
    return "Not graded";
  }

  return possible > 0 ? `${score}/${possible}` : String(score);
}

function formatPercentage(value, pointsPossible) {
  const score = Number(value);
  const possible = Number(pointsPossible || 0);

  if (!Number.isFinite(score) || !Number.isFinite(possible) || possible <= 0) {
    return "";
  }

  return `${((score / possible) * 100).toFixed(1)}%`;
}

function renderPill(value, fallback = "Not set") {
  return value ? value : fallback;
}

function normalizeGeneratedStudentLoginName(name = "") {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[\s.]+/g, "")
    .replace(/[^a-z0-9_-]+/g, "")
    .slice(0, 64);
}

function getGeneratedStudentLoginName(student = {}) {
  const displayLogin = normalizeGeneratedStudentLoginName(student.displayName);
  return (
    (displayLogin && displayLogin !== "newstudent" ? displayLogin : "") ||
    normalizeGeneratedStudentLoginName(
      [
        student.preferredFirstName || student.legalFirstName,
        student.preferredLastName || student.legalLastName,
      ]
        .filter(Boolean)
        .join(" ")
    )
  );
}

function renderKeyValues(data = {}) {
  const hiddenKeys = new Set([
    "eduStudentDeviceStudentId",
    "eduStudentId",
    "deviceLoginName",
    "devicePin",
  ]);

  return Object.entries(data).filter(([key, value]) => {
    if (hiddenKeys.has(key)) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "boolean") return true;
    return value !== null && value !== undefined && value !== "";
  });
}

function toJsonText(value, fallback = "[]") {
  try {
    return JSON.stringify(value ?? JSON.parse(fallback), null, 2);
  } catch (_error) {
    return fallback;
  }
}

function parseJsonField(value, fallback) {
  try {
    const parsed = JSON.parse(value);
    return parsed;
  } catch (_error) {
    return fallback;
  }
}

function cloneValue(value, fallback) {
  try {
    return JSON.parse(JSON.stringify(value ?? fallback));
  } catch (_error) {
    return fallback;
  }
}

function normalizeText(value) {
  return String(value || "").trim();
}

function normalizeLowerText(value) {
  return normalizeText(value).toLowerCase();
}

function getGuardianIdentityKey(guardian = {}, fallback = "") {
  const linkedUserId = normalizeLowerText(guardian.linkedUserId);
  const email = normalizeLowerText(guardian.email);
  const phone = normalizeText(guardian.phone).replace(/\D+/g, "");
  const name = normalizeLowerText(guardian.name);

  if (linkedUserId) return `user:${linkedUserId}`;
  if (email) return `email:${email}`;
  if (phone && name) return `phone-name:${phone}:${name}`;
  if (phone) return `phone:${phone}`;
  if (name) return `name:${name}`;
  return fallback;
}

function getSubjectTeacherAssignments(value = {}) {
  return Array.isArray(value?.customFields?.subjectTeachers)
    ? value.customFields.subjectTeachers
    : [];
}

function getEmergencyContactIdentityKey(contact = {}, fallback = "") {
  const phone = normalizeText(contact.phone).replace(/\D+/g, "");
  const name = normalizeLowerText(contact.name);
  const relationship = normalizeLowerText(contact.relationship);

  if (phone && name) return `phone-name:${phone}:${name}`;
  if (phone) return `phone:${phone}`;
  if (name && relationship) return `name-relationship:${name}:${relationship}`;
  if (name) return `name:${name}`;
  return fallback;
}

function createGuardian() {
  return {
    name: "",
    relationship: "",
    phone: "",
    email: "",
    linkedUserId: "",
    portalAccess: false,
    pickupApproved: false,
  };
}

function createEmergencyContact() {
  return {
    name: "",
    relationship: "",
    phone: "",
    priority: "",
  };
}

function createSubjectTeacherAssignment() {
  return {
    subjectName: "",
    teacherStaffId: "",
    teacherUserId: "",
    teacherDisplayName: "",
  };
}

function createDisciplinaryEntry() {
  return {
    date: "",
    incidentType: "",
    actionTaken: "",
    staffName: "",
    notes: "",
  };
}

function createParentContactEntry() {
  return {
    date: "",
    method: "",
    contactName: "",
    staffName: "",
    summary: "",
  };
}

function getTuitionProfile(student = {}) {
  const tuition = student?.customFields?.tuition || {};
  return {
    familyBillingGroupId: normalizeText(tuition.familyBillingGroupId),
    familyBillingName: normalizeText(tuition.familyBillingName),
    payerName: normalizeText(tuition.payerName),
    payerEmail: normalizeText(tuition.payerEmail),
    payerPhone: normalizeText(tuition.payerPhone),
    fees: Array.isArray(tuition.fees) ? tuition.fees : [],
    payments: Array.isArray(tuition.payments) ? tuition.payments : [],
    generatedBills: Array.isArray(tuition.generatedBills) ? tuition.generatedBills : [],
    stripeInvoiceSync: tuition.stripeInvoiceSync || {},
    billingStarted: tuition.billingStarted === true,
    planId: normalizeText(tuition.planId),
    planLabel: normalizeText(tuition.planLabel),
    notes: normalizeText(tuition.notes),
  };
}

function createTuitionFee() {
  return {
    id: `fee-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    date: new Date().toISOString().slice(0, 10),
    label: "",
    amountCents: 0,
    status: "open",
    notes: "",
  };
}

function createTuitionPayment() {
  return {
    id: `payment-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    date: new Date().toISOString().slice(0, 10),
    method: "",
    amountCents: 0,
    reference: "",
    notes: "",
  };
}

function centsFromDollars(value) {
  const normalized = Number.parseFloat(String(value || "0").replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(normalized) ? Math.max(0, Math.round(normalized * 100)) : 0;
}

function dollarsFromCents(value) {
  return ((Number(value || 0) || 0) / 100).toFixed(2);
}

function getFamilyBillingStudents(students = [], selectedStudent = {}) {
  const profile = getTuitionProfile(selectedStudent);
  const groupId = normalizeLowerText(profile.familyBillingGroupId);
  const householdName = normalizeLowerText(selectedStudent.householdName);
  const primaryEmail = normalizeLowerText(selectedStudent.primaryEmail);

  return students.filter((student) => {
    if (!student?.id || student.id === selectedStudent.id) {
      return false;
    }

    const studentProfile = getTuitionProfile(student);
    return (
      (groupId && normalizeLowerText(studentProfile.familyBillingGroupId) === groupId) ||
      (!groupId && householdName && normalizeLowerText(student.householdName) === householdName) ||
      (!groupId && primaryEmail && normalizeLowerText(student.primaryEmail) === primaryEmail)
    );
  });
}

function getPrimaryGuardian(student = {}) {
  const guardians = Array.isArray(student.guardians) ? student.guardians : [];
  return (
    guardians.find((guardian) => normalizeText(guardian.email)) ||
    guardians.find((guardian) => normalizeText(guardian.phone)) ||
    guardians[0] ||
    {}
  );
}

function calculateTuitionBalance(student = {}) {
  const profile = getTuitionProfile(student);
  const generatedBillBalance = profile.generatedBills.reduce((sum, bill) => {
    if (normalizeLowerText(bill.status) === "paid") {
      return sum;
    }

    const remaining =
      bill.remainingAmountCents !== undefined
        ? Number(bill.remainingAmountCents || 0)
        : Number(bill.amountCents || 0) - Number(bill.paidAmountCents || 0);
    return sum + Math.max(0, remaining || 0);
  }, 0);
  const openFees = profile.fees.reduce((sum, fee) => {
    if (["paid", "waived", "void"].includes(normalizeLowerText(fee.status))) {
      return sum;
    }

    return sum + (Number(fee.amountCents || 0) || 0);
  }, 0);
  const payments = profile.payments.reduce(
    (sum, payment) =>
      payment.billId ? sum : sum + (Number(payment.amountCents || 0) || 0),
    0
  );
  const startingBalance = profile.generatedBills.length
    ? generatedBillBalance
    : Number(student.tuitionBalanceCents || 0) || 0;

  return Math.max(0, startingBalance + openFees - payments);
}

function deriveTuitionStatus(student = {}) {
  const profile = getTuitionProfile(student);
  return profile.planLabel || profile.planId || student.tuitionPaymentStatus || "";
}

function SectionCard({ icon: Icon, title, action = null, sectionRef = null, children }) {
  return (
    <section ref={sectionRef} style={styles.sectionCard}>
      <div style={styles.sectionHeader}>
        <div style={styles.sectionHeaderLeft}>
          <div style={styles.sectionIcon}>
            <Icon size={18} />
          </div>
          <div style={styles.sectionTitle}>{title}</div>
        </div>
        {action}
      </div>
      <div style={styles.sectionBody}>{children}</div>
    </section>
  );
}

function DetailGrid({ items = [] }) {
  return (
    <div style={styles.detailGrid}>
      {items.map((item) => (
        <div key={item.label} style={styles.detailCard}>
          <div style={styles.detailLabel}>{item.label}</div>
          <div style={styles.detailValue}>{item.value || "Not set"}</div>
        </div>
      ))}
    </div>
  );
}

function LabeledInput({ label, value, onChange, placeholder = "", type = "text" }) {
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        style={styles.input}
      />
    </label>
  );
}

function LabeledSelect({ label, value, onChange, options = [], placeholder = "Select an option" }) {
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      <select value={value || ""} onChange={(event) => onChange(event.target.value)} style={styles.input}>
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function LabeledTextarea({ label, value, onChange, placeholder = "", rows = 5 }) {
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      <textarea
        rows={rows}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        style={styles.textarea}
      />
    </label>
  );
}

function MoneyInput({ label, valueCents, onChangeCents }) {
  const [text, setText] = useState(dollarsFromCents(valueCents));

  useEffect(() => {
    setText(dollarsFromCents(valueCents));
  }, [valueCents]);

  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      <input
        type="text"
        inputMode="decimal"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          onChangeCents(centsFromDollars(event.target.value));
        }}
        onBlur={() => setText(dollarsFromCents(centsFromDollars(text)))}
        placeholder="0.00"
        style={styles.input}
      />
    </label>
  );
}

export default function StudentsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tuitionFilter, setTuitionFilter] = useState("all");
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [previewStudentId, setPreviewStudentId] = useState("");
  const [pendingEditStudentId, setPendingEditStudentId] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [activeEditSection, setActiveEditSection] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editForm, setEditForm] = useState(null);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [teacherStaffOptions, setTeacherStaffOptions] = useState([]);
  const [academicRecords, setAcademicRecords] = useState({
    schemaReady: true,
    assignments: [],
    grades: [],
    reports: [],
  });
  const [academicLoading, setAcademicLoading] = useState(false);
  const [guardianLinkSelection, setGuardianLinkSelection] = useState("");
  const [emergencyContactLinkSelection, setEmergencyContactLinkSelection] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [guardianAccessSavingKey, setGuardianAccessSavingKey] = useState("");
  const studentPhotoInputRef = useRef(null);
  const editSectionRefs = useMemo(
    () => ({
      demographics: { current: null },
      enrollment: { current: null },
      tuition: { current: null },
      household: { current: null },
      guardians: { current: null },
      emergency: { current: null },
      medical: { current: null },
      disciplinary: { current: null },
      parentContact: { current: null },
      programs: { current: null },
      account: { current: null },
    }),
    []
  );

  useEffect(() => {
    let mounted = true;

    async function bootstrap() {
      setLoading(true);

      try {
        const nextStudents = await loadCampusStudents(user?.id);
        let nextTeacherOptions = [];
        let nextTeacherStaffOptions = [];

        try {
          const staffDashboard = await loadCampusStaffDashboard(user?.id);
          const activeTeachers = (staffDashboard.staff || []).filter(
            (staff) =>
              staff.isActive &&
              String(staff.staffType || "").toLowerCase() === "teacher" &&
              String(staff.displayName || "").trim()
          );

          nextTeacherOptions = (staffDashboard.staff || [])
            .filter(
              (staff) =>
                staff.isActive &&
                String(staff.staffType || "").toLowerCase() === "teacher" &&
                String(staff.displayName || "").trim()
            )
            .map((staff) => String(staff.displayName || "").trim())
            .filter((name, index, list) => list.indexOf(name) === index)
            .sort((a, b) => a.localeCompare(b))
            .map((name) => ({ label: name, value: name }));
          nextTeacherStaffOptions = activeTeachers
            .sort((a, b) =>
              String(a.displayName || "").localeCompare(String(b.displayName || ""))
            )
            .map((staff) => ({
              label: String(staff.displayName || "").trim(),
              value: String(staff.id || ""),
              teacherUserId: String(staff.linkedUserId || ""),
            }));
        } catch (staffError) {
          console.error("Campus students teacher list load error:", staffError);
        }

        if (!mounted) return;
        setStudents(nextStudents);
        setTeacherOptions(nextTeacherOptions);
        setTeacherStaffOptions(nextTeacherStaffOptions);
        setSelectedStudentId("");
        setPreviewStudentId(nextStudents[0]?.id || "");
      } catch (loadError) {
        console.error("Campus students page load error:", loadError);
        if (!mounted) return;
        setStudents([]);
        setTeacherOptions([]);
        setTeacherStaffOptions([]);
      } finally {
        if (mounted) {
          setLoading(false);
          setBootstrapped(true);
        }
      }
    }

    bootstrap();

    return () => {
      mounted = false;
    };
  }, [user?.id]);

  useEffect(() => {
    let mounted = true;

    async function loadRecords() {
      if (!selectedStudentId || !user?.id) {
        setAcademicRecords({
          schemaReady: true,
          assignments: [],
          grades: [],
          reports: [],
        });
        return;
      }

      setAcademicLoading(true);

      try {
        const records = await loadCampusStudentAcademicRecords(user.id, selectedStudentId);
        if (!mounted) return;
        setAcademicRecords(records);
      } catch (recordsError) {
        console.error("Campus student academic records load error:", recordsError);
        if (!mounted) return;
        setAcademicRecords({
          schemaReady: true,
          assignments: [],
          grades: [],
          reports: [],
        });
      } finally {
        if (mounted) {
          setAcademicLoading(false);
        }
      }
    }

    loadRecords();

    return () => {
      mounted = false;
    };
  }, [selectedStudentId, user?.id]);

  const filteredStudents = useMemo(() => {
    const searched = searchCampusStudents(students, query);

    return searched.filter((student) => {
      const status = (student.currentEnrollmentStatus || student.enrollmentStatus || "").toLowerCase();
      const tuitionStatus = (student.tuitionPaymentStatus || "").toLowerCase();

      const statusMatches =
        statusFilter === "all" ||
        (statusFilter === "current" && status === "currently enrolled") ||
        (statusFilter === "active" && student.isActive) ||
        (statusFilter === "inactive" && !student.isActive);

      const tuitionMatches =
        tuitionFilter === "all" ||
        (tuitionFilter === "past-due" && tuitionStatus === "past due") ||
        (tuitionFilter === "current" && tuitionStatus === "current") ||
        (tuitionFilter === "no-status" && !tuitionStatus);

      return statusMatches && tuitionMatches;
    });
  }, [query, statusFilter, students, tuitionFilter]);

  const selectedStudent = selectedStudentId
    ? filteredStudents.find((student) => student.id === selectedStudentId) ||
      students.find((student) => student.id === selectedStudentId) ||
      null
    : null;

  const previewStudent = previewStudentId
    ? filteredStudents.find((student) => student.id === previewStudentId) ||
      students.find((student) => student.id === previewStudentId) ||
      null
    : filteredStudents[0] || students[0] || null;

  const selectedTuitionProfile = selectedStudent ? getTuitionProfile(selectedStudent) : getTuitionProfile();
  const selectedFamilyBillingStudents = selectedStudent
    ? getFamilyBillingStudents(students, selectedStudent)
    : [];

  const reusableGuardianOptions = useMemo(() => {
    const currentStudentId = editForm?.id || selectedStudent?.id || "";
    const guardianMap = new Map();

    students.forEach((student) => {
      if (!student?.id || student.id === currentStudentId) {
        return;
      }

      (student.guardians || []).forEach((guardian, index) => {
        if (
          !normalizeText(guardian?.name) &&
          !normalizeText(guardian?.email) &&
          !normalizeText(guardian?.phone)
        ) {
          return;
        }

        const key = getGuardianIdentityKey(guardian, `student:${student.id}:guardian:${index}`);
        const sourceStudentName = normalizeText(student.displayName) || "Student";
        const existing = guardianMap.get(key);

        if (existing) {
          if (!existing.sourceStudentNames.includes(sourceStudentName)) {
            existing.sourceStudentNames.push(sourceStudentName);
          }
          return;
        }

        guardianMap.set(key, {
          key,
          label: normalizeText(guardian.name) || normalizeText(guardian.email) || "Guardian",
          sourceStudentNames: [sourceStudentName],
          sourceStudent: {
            householdName: normalizeText(student.householdName),
            primaryPhone: normalizeText(student.primaryPhone),
            primaryEmail: normalizeText(student.primaryEmail),
            streetAddress1: normalizeText(student.streetAddress1),
            streetAddress2: normalizeText(student.streetAddress2),
            city: normalizeText(student.city),
            state: normalizeText(student.state),
            postalCode: normalizeText(student.postalCode),
          },
          guardian: {
            name: normalizeText(guardian.name),
            relationship: normalizeText(guardian.relationship),
            phone: normalizeText(guardian.phone),
            email: normalizeText(guardian.email),
            linkedUserId: normalizeText(guardian.linkedUserId),
            portalAccess: Boolean(guardian.portalAccess),
            pickupApproved: Boolean(guardian.pickupApproved),
          },
        });
      });
    });

    return Array.from(guardianMap.values())
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((option) => ({
        ...option,
        description: option.sourceStudentNames.join(", "),
      }));
  }, [editForm?.id, selectedStudent?.id, students]);

  const reusableEmergencyContactOptions = useMemo(() => {
    const currentStudentId = editForm?.id || selectedStudent?.id || "";
    const contactMap = new Map();

    students.forEach((student) => {
      if (!student?.id || student.id === currentStudentId) {
        return;
      }

      (student.emergencyContacts || []).forEach((contact, index) => {
        if (!normalizeText(contact?.name) && !normalizeText(contact?.phone)) {
          return;
        }

        const key = getEmergencyContactIdentityKey(contact, `student:${student.id}:contact:${index}`);
        const sourceStudentName = normalizeText(student.displayName) || "Student";
        const existing = contactMap.get(key);

        if (existing) {
          if (!existing.sourceStudentNames.includes(sourceStudentName)) {
            existing.sourceStudentNames.push(sourceStudentName);
          }
          return;
        }

        contactMap.set(key, {
          key,
          label: normalizeText(contact.name) || normalizeText(contact.phone) || "Emergency Contact",
          sourceStudentNames: [sourceStudentName],
          contact: {
            name: normalizeText(contact.name),
            relationship: normalizeText(contact.relationship),
            phone: normalizeText(contact.phone),
            priority: normalizeText(contact.priority),
          },
        });
      });
    });

    return Array.from(contactMap.values())
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((option) => ({
        ...option,
        description: option.sourceStudentNames.join(", "),
      }));
  }, [editForm?.id, selectedStudent?.id, students]);

  useEffect(() => {
    if (!selectedStudent || editMode) {
      return;
    }

    setEditForm({
      ...selectedStudent,
      raceEthnicityText: (selectedStudent.raceEthnicity || []).join(", "),
      tagsText: (selectedStudent.tags || []).join(", "),
      guardians: cloneValue(selectedStudent.guardians, []),
      emergencyContacts: cloneValue(selectedStudent.emergencyContacts, []),
      medical: cloneValue(selectedStudent.medical, {}),
      disciplinary: cloneValue(selectedStudent.disciplinary, []),
      parentContactLog: cloneValue(selectedStudent.parentContactLog, []),
      demographics: cloneValue(selectedStudent.demographics, {}),
      enrollment: cloneValue(selectedStudent.enrollment, {}),
      programParticipation: cloneValue(selectedStudent.programParticipation, {}),
      customFields: cloneValue(selectedStudent.customFields, {}),
    });
  }, [selectedStudent, editMode]);

  useEffect(() => {
    if (!selectedStudent || pendingEditStudentId !== selectedStudent.id) {
      return;
    }

    setPendingEditStudentId("");
    beginEdit();
  }, [pendingEditStudentId, selectedStudent]);

  useEffect(() => {
    if (!editMode || !activeEditSection) {
      return;
    }

    const target = editSectionRefs[activeEditSection]?.current;
    if (!target) {
      return;
    }

    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [activeEditSection, editMode, editSectionRefs]);

  function beginEdit(sectionKey = "demographics") {
    if (!selectedStudent) return;
    setError("");
    setNotice("");
    setActiveEditSection(sectionKey || "demographics");
    setGuardianLinkSelection("");
    setEmergencyContactLinkSelection("");
    setEditMode(true);
    setEditForm({
      ...selectedStudent,
      raceEthnicityText: (selectedStudent.raceEthnicity || []).join(", "),
      tagsText: (selectedStudent.tags || []).join(", "),
      guardians: cloneValue(selectedStudent.guardians, []),
      emergencyContacts: cloneValue(selectedStudent.emergencyContacts, []),
      medical: cloneValue(selectedStudent.medical, {}),
      disciplinary: cloneValue(selectedStudent.disciplinary, []),
      parentContactLog: cloneValue(selectedStudent.parentContactLog, []),
      demographics: cloneValue(selectedStudent.demographics, {}),
      enrollment: cloneValue(selectedStudent.enrollment, {}),
      programParticipation: cloneValue(selectedStudent.programParticipation, {}),
      customFields: cloneValue(selectedStudent.customFields, {}),
    });
  }

  function cancelEdit() {
    setEditMode(false);
    setActiveEditSection("");
  }

  function renderSectionEditAction(sectionKey) {
    return (
      <button type="button" style={styles.sectionEditButton} onClick={() => beginEdit(sectionKey)}>
        <PencilLine size={14} />
        Edit
      </button>
    );
  }

  function updateForm(key, value) {
    setEditForm((current) => {
      const next = {
        ...current,
        [key]: value,
      };

      if (["displayName", "preferredFirstName", "preferredLastName", "legalFirstName", "legalLastName"].includes(key)) {
        const currentLogin = normalizeGeneratedStudentLoginName(current?.deviceLoginName);
        const previousGeneratedLogin = getGeneratedStudentLoginName(current);
        const nextGeneratedLogin = getGeneratedStudentLoginName(next);

        if (
          nextGeneratedLogin &&
          (!currentLogin || currentLogin === "newstudent" || currentLogin === previousGeneratedLogin)
        ) {
          next.deviceLoginName = nextGeneratedLogin;
        }
      }

      return next;
    });
  }

  function updateTuitionProfileField(field, value) {
    setEditForm((current) => ({
      ...current,
      customFields: {
        ...(current?.customFields || {}),
        tuition: {
          ...(current?.customFields?.tuition || {}),
          [field]: value,
        },
      },
    }));
  }

  function addTuitionFee() {
    setEditForm((current) => {
      const profile = getTuitionProfile(current);
      return {
        ...current,
        customFields: {
          ...(current?.customFields || {}),
          tuition: {
            ...(current?.customFields?.tuition || {}),
            fees: [...profile.fees, createTuitionFee()],
          },
        },
      };
    });
  }

  function updateTuitionFee(index, updates) {
    setEditForm((current) => {
      const profile = getTuitionProfile(current);
      return {
        ...current,
        customFields: {
          ...(current?.customFields || {}),
          tuition: {
            ...(current?.customFields?.tuition || {}),
            fees: profile.fees.map((fee, feeIndex) =>
              feeIndex === index ? { ...fee, ...updates } : fee
            ),
          },
        },
      };
    });
  }

  function removeTuitionFee(index) {
    setEditForm((current) => {
      const profile = getTuitionProfile(current);
      return {
        ...current,
        customFields: {
          ...(current?.customFields || {}),
          tuition: {
            ...(current?.customFields?.tuition || {}),
            fees: profile.fees.filter((_fee, feeIndex) => feeIndex !== index),
          },
        },
      };
    });
  }

  function addTuitionPayment() {
    setEditForm((current) => {
      const profile = getTuitionProfile(current);
      return {
        ...current,
        customFields: {
          ...(current?.customFields || {}),
          tuition: {
            ...(current?.customFields?.tuition || {}),
            payments: [...profile.payments, createTuitionPayment()],
          },
        },
      };
    });
  }

  function updateTuitionPayment(index, updates) {
    setEditForm((current) => {
      const profile = getTuitionProfile(current);
      return {
        ...current,
        customFields: {
          ...(current?.customFields || {}),
          tuition: {
            ...(current?.customFields?.tuition || {}),
            payments: profile.payments.map((payment, paymentIndex) =>
              paymentIndex === index ? { ...payment, ...updates } : payment
            ),
          },
        },
      };
    });
  }

  function removeTuitionPayment(index) {
    setEditForm((current) => {
      const profile = getTuitionProfile(current);
      return {
        ...current,
        customFields: {
          ...(current?.customFields || {}),
          tuition: {
            ...(current?.customFields?.tuition || {}),
            payments: profile.payments.filter((_payment, paymentIndex) => paymentIndex !== index),
          },
        },
      };
    });
  }

  function updateTuitionBill(index, updates) {
    setEditForm((current) => {
      const profile = getTuitionProfile(current);
      return {
        ...current,
        customFields: {
          ...(current?.customFields || {}),
          tuition: {
            ...(current?.customFields?.tuition || {}),
            generatedBills: profile.generatedBills.map((bill, billIndex) =>
              billIndex === index ? { ...bill, ...updates } : bill
            ),
          },
        },
      };
    });
  }

  function markTuitionBillPaid(index, method) {
    setEditForm((current) => {
      const profile = getTuitionProfile(current);
      const bill = profile.generatedBills[index] || {};
      const amountCents = Number(bill.remainingAmountCents ?? bill.amountCents ?? 0) || 0;
      const payment = {
        ...createTuitionPayment(),
        method,
        amountCents,
        billId: bill.id || "",
        reference: bill.label || "",
        notes: `Marked ${bill.label || "invoice"} paid by ${method}.`,
      };

      return {
        ...current,
        customFields: {
          ...(current?.customFields || {}),
          tuition: {
            ...(current?.customFields?.tuition || {}),
            generatedBills: profile.generatedBills.map((nextBill, billIndex) =>
              billIndex === index
                ? {
                    ...nextBill,
                    status: "paid",
                    paidAmountCents: Number(nextBill.amountCents || 0) || amountCents,
                    remainingAmountCents: 0,
                    paidDate: payment.date,
                    paymentMethod: method,
                  }
                : nextBill
            ),
            payments: [...profile.payments, payment],
            billingStarted: true,
          },
        },
      };
    });
  }

  function useHouseholdForTuitionBilling() {
    setEditForm((current) => {
      const guardian = getPrimaryGuardian(current);
      const householdName = normalizeText(current?.householdName);
      const primaryEmail = normalizeText(current?.primaryEmail);
      const groupSeed = householdName || primaryEmail || normalizeText(current?.displayName);
      const groupId = normalizeLowerText(groupSeed).replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

      return {
        ...current,
        customFields: {
          ...(current?.customFields || {}),
          tuition: {
            ...(current?.customFields?.tuition || {}),
            familyBillingGroupId: groupId,
            familyBillingName: householdName || `${current?.displayName || "Student"} Family`,
            payerName: normalizeText(guardian.name) || normalizeText(current?.displayName),
            payerEmail: normalizeText(guardian.email) || primaryEmail,
            payerPhone: normalizeText(guardian.phone) || normalizeText(current?.primaryPhone),
          },
        },
      };
    });
  }

  function updateListItem(key, index, field, value) {
    setEditForm((current) => {
      const nextItems = Array.isArray(current?.[key]) ? [...current[key]] : [];
      nextItems[index] = {
        ...(nextItems[index] || {}),
        [field]: value,
      };

      return {
        ...current,
        [key]: nextItems,
      };
    });
  }

  function addListItem(key, factory) {
    setEditForm((current) => ({
      ...current,
      [key]: [...(Array.isArray(current?.[key]) ? current[key] : []), factory()],
    }));
  }

  function removeListItem(key, index) {
    setEditForm((current) => ({
      ...current,
      [key]: (Array.isArray(current?.[key]) ? current[key] : []).filter(
        (_item, itemIndex) => itemIndex !== index
      ),
    }));
  }

  function handleLinkExistingGuardian() {
    if (!guardianLinkSelection) {
      return;
    }

    const selectedGuardian = reusableGuardianOptions.find(
      (option) => option.key === guardianLinkSelection
    );

    if (!selectedGuardian) {
      return;
    }

    const nextGuardian = cloneValue(selectedGuardian.guardian, createGuardian());
    const identityKey = getGuardianIdentityKey(nextGuardian);
    let wasDuplicate = false;

    setEditForm((current) => {
      const currentGuardians = Array.isArray(current?.guardians) ? [...current.guardians] : [];
      const hasMatch = currentGuardians.some(
        (guardian) => getGuardianIdentityKey(guardian) === identityKey
      );

      if (hasMatch) {
        wasDuplicate = true;
        return current;
      }

      const blankIndex = currentGuardians.findIndex(
        (guardian) =>
          !normalizeText(guardian?.name) &&
          !normalizeText(guardian?.email) &&
          !normalizeText(guardian?.phone) &&
          !normalizeText(guardian?.linkedUserId)
      );

      if (blankIndex >= 0) {
        currentGuardians[blankIndex] = nextGuardian;
      } else {
        currentGuardians.push(nextGuardian);
      }

      return {
        ...current,
        householdName: normalizeText(current?.householdName) || selectedGuardian.sourceStudent?.householdName || "",
        primaryPhone: normalizeText(current?.primaryPhone) || selectedGuardian.sourceStudent?.primaryPhone || "",
        primaryEmail: normalizeText(current?.primaryEmail) || selectedGuardian.sourceStudent?.primaryEmail || "",
        streetAddress1: normalizeText(current?.streetAddress1) || selectedGuardian.sourceStudent?.streetAddress1 || "",
        streetAddress2: normalizeText(current?.streetAddress2) || selectedGuardian.sourceStudent?.streetAddress2 || "",
        city: normalizeText(current?.city) || selectedGuardian.sourceStudent?.city || "",
        state: normalizeText(current?.state) || selectedGuardian.sourceStudent?.state || "",
        postalCode: normalizeText(current?.postalCode) || selectedGuardian.sourceStudent?.postalCode || "",
        guardians: currentGuardians,
      };
    });

    setGuardianLinkSelection("");
    setNotice(
      wasDuplicate
        ? "That guardian is already connected to this student."
        : `Guardian linked from ${selectedGuardian.description}. Save the student record to keep the connection.`
    );
  }

  function handleLinkExistingEmergencyContact() {
    if (!emergencyContactLinkSelection) {
      return;
    }

    const selectedContact = reusableEmergencyContactOptions.find(
      (option) => option.key === emergencyContactLinkSelection
    );

    if (!selectedContact) {
      return;
    }

    const nextContact = cloneValue(selectedContact.contact, createEmergencyContact());
    const identityKey = getEmergencyContactIdentityKey(nextContact);
    let wasDuplicate = false;

    setEditForm((current) => {
      const currentContacts = Array.isArray(current?.emergencyContacts)
        ? [...current.emergencyContacts]
        : [];
      const hasMatch = currentContacts.some(
        (contact) => getEmergencyContactIdentityKey(contact) === identityKey
      );

      if (hasMatch) {
        wasDuplicate = true;
        return current;
      }

      const blankIndex = currentContacts.findIndex(
        (contact) =>
          !normalizeText(contact?.name) &&
          !normalizeText(contact?.phone) &&
          !normalizeText(contact?.relationship)
      );

      if (blankIndex >= 0) {
        currentContacts[blankIndex] = nextContact;
      } else {
        currentContacts.push(nextContact);
      }

      return {
        ...current,
        emergencyContacts: currentContacts,
      };
    });

    setEmergencyContactLinkSelection("");
    setNotice(
      wasDuplicate
        ? "That emergency contact is already connected to this student."
        : `Emergency contact linked from ${selectedContact.description}. Save the student record to keep the connection.`
    );
  }

  function updateNestedSection(section, field, value) {
    setEditForm((current) => ({
      ...current,
      [section]: {
        ...(current?.[section] || {}),
        [field]: value,
      },
    }));
  }

  function updateSubjectTeacherAssignment(index, field, value) {
    setEditForm((current) => {
      const currentAssignments = getSubjectTeacherAssignments(current);
      const nextAssignments = [...currentAssignments];
      const nextItem = {
        ...(nextAssignments[index] || createSubjectTeacherAssignment()),
        [field]: value,
      };

      if (field === "teacherStaffId") {
        const matchedTeacher = teacherStaffOptions.find((option) => option.value === value);
        nextItem.teacherDisplayName = matchedTeacher?.label || "";
        nextItem.teacherUserId = matchedTeacher?.teacherUserId || "";
      }

      nextAssignments[index] = nextItem;

      return {
        ...current,
        customFields: {
          ...(current?.customFields || {}),
          subjectTeachers: nextAssignments,
        },
      };
    });
  }

  function addSubjectTeacherAssignment() {
    setEditForm((current) => ({
      ...current,
      customFields: {
        ...(current?.customFields || {}),
        subjectTeachers: [...getSubjectTeacherAssignments(current), createSubjectTeacherAssignment()],
      },
    }));
  }

  function removeSubjectTeacherAssignment(index) {
    setEditForm((current) => ({
      ...current,
      customFields: {
        ...(current?.customFields || {}),
        subjectTeachers: getSubjectTeacherAssignments(current).filter(
          (_item, itemIndex) => itemIndex !== index
        ),
      },
    }));
  }

  function syncStudentRecord(nextStudent, noticeMessage = "") {
    setStudents((current) =>
      current.map((student) => (student.id === nextStudent.id ? nextStudent : student))
    );
    setSelectedStudentId(nextStudent.id);
    setPreviewStudentId(nextStudent.id);
    setEditForm((current) =>
      current && current.id === nextStudent.id
        ? {
            ...nextStudent,
            raceEthnicityText: (nextStudent.raceEthnicity || []).join(", "),
            tagsText: (nextStudent.tags || []).join(", "),
            guardians: cloneValue(nextStudent.guardians, []),
            emergencyContacts: cloneValue(nextStudent.emergencyContacts, []),
            medical: cloneValue(nextStudent.medical, {}),
            disciplinary: cloneValue(nextStudent.disciplinary, []),
            parentContactLog: cloneValue(nextStudent.parentContactLog, []),
            demographics: cloneValue(nextStudent.demographics, {}),
            enrollment: cloneValue(nextStudent.enrollment, {}),
            programParticipation: cloneValue(nextStudent.programParticipation, {}),
            customFields: cloneValue(nextStudent.customFields, {}),
          }
        : current
    );

    if (noticeMessage) {
      setNotice(noticeMessage);
    }
  }

  async function saveStudent() {
    if (!editForm) return;

    setSaving(true);
    setError("");
    setNotice("");

    try {
      const tuitionBalanceCents = calculateTuitionBalance(editForm);
      const tuitionPaymentStatus = deriveTuitionStatus({
        ...editForm,
        tuitionBalanceCents,
      });
      const nextStudent = await updateCampusStudent(user?.id, {
        ...editForm,
        raceEthnicity: editForm.raceEthnicityText
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
        tags: editForm.tagsText
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
        guardians: editForm.guardians || [],
        emergencyContacts: editForm.emergencyContacts || [],
        medical: editForm.medical || {},
        disciplinary: editForm.disciplinary || [],
        parentContactLog: editForm.parentContactLog || [],
        demographics: editForm.demographics || {},
        enrollment: editForm.enrollment || {},
        programParticipation: editForm.programParticipation || {},
        tuitionBalanceCents,
        tuitionPaymentStatus,
        customFields: editForm.customFields || {},
      });

      syncStudentRecord(nextStudent);
      setEditMode(false);
      setActiveEditSection("");
      setNotice("Student section updated.");
    } catch (saveError) {
      console.error("Student save error:", saveError);
      setError(saveError?.message || "Could not save student.");
    } finally {
      setSaving(false);
    }
  }

  async function handleStudentPhotoSelected(event) {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file || !user?.id || !selectedStudent?.id) {
      return;
    }

    try {
      setPhotoUploading(true);
      setError("");
      setNotice("");
      const nextStudent = await uploadCampusStudentPhoto(user.id, selectedStudent.id, file);

      syncStudentRecord(nextStudent, "Student photo updated.");
    } catch (uploadError) {
      console.error("Student photo upload error:", uploadError);
      setError(uploadError?.message || "Could not upload student photo.");
    } finally {
      setPhotoUploading(false);
    }
  }

  async function handleAddStudent() {
    if (!user?.id) {
      return;
    }

    try {
      setSaving(true);
      setError("");
      setNotice("");
      const createdStudent = await createCampusStudent(user.id);
      setStudents((current) => [createdStudent, ...current]);
      setSelectedStudentId(createdStudent.id);
      setPreviewStudentId(createdStudent.id);
      setPendingEditStudentId(createdStudent.id);
      setNotice("Student record created.");
    } catch (createError) {
      console.error("Campus student create error:", createError);
      setError(createError?.message || "Could not create student.");
    } finally {
      setSaving(false);
    }
  }

  async function handleGuardianInvite(guardianIndex) {
    const workingStudent = editForm || selectedStudent;
    const guardian = workingStudent?.guardians?.[guardianIndex];

    if (!user?.id || !workingStudent?.id || !guardian) {
      return;
    }

    try {
      setGuardianAccessSavingKey(`invite:${guardianIndex}`);
      setError("");
      setNotice("");
      const { result, student: nextStudent } = await inviteCampusParentPortal({
        userId: user.id,
        student: workingStudent,
        guardianIndex,
      });

      if (nextStudent?.id) {
        syncStudentRecord(nextStudent);
      }

      setNotice(result?.message || `Parent portal invite sent to ${guardian.email}.`);
    } catch (inviteError) {
      console.error("Campus parent invite error:", inviteError);
      setError(inviteError.message || "Could not send the parent portal invite.");
    } finally {
      setGuardianAccessSavingKey("");
    }
  }

  async function handleGuardianPortalAccess(guardianIndex, enabled) {
    let workingStudent = editForm || selectedStudent;
    let guardian = workingStudent?.guardians?.[guardianIndex];

    if (!user?.id || !workingStudent?.id || !workingStudent?.accountId || !guardian) {
      return;
    }

    try {
      setGuardianAccessSavingKey(`access:${guardianIndex}`);
      setError("");
      setNotice("");

      if (!guardian.linkedUserId) {
        const { result, student: invitedStudent, guardian: invitedGuardian } =
          await inviteCampusParentPortal({
            userId: user.id,
            student: workingStudent,
            guardianIndex,
          });

        if (invitedStudent?.id) {
          syncStudentRecord(invitedStudent);
          workingStudent = invitedStudent;
          guardian = invitedStudent?.guardians?.[guardianIndex] || invitedGuardian || guardian;
        }

        if (!guardian?.linkedUserId) {
          throw new Error(
            result?.message ||
              "The parent account could not be linked yet. Ask the parent to complete their invite email first, then try again."
          );
        }
      }

      await setCampusParentPortalAccess({
        userId: user.id,
        accountId: workingStudent.accountId,
        targetUserId: guardian.linkedUserId,
        enabled,
      });

      const nextGuardians = cloneValue(workingStudent.guardians, []);
      nextGuardians[guardianIndex] = {
        ...nextGuardians[guardianIndex],
        portalAccess: enabled,
      };

      const nextStudent = await updateCampusStudent(user.id, {
        ...workingStudent,
        guardians: nextGuardians,
      });

      syncStudentRecord(
        nextStudent,
        enabled ? "Parent portal access enabled." : "Parent portal access removed."
      );
    } catch (accessError) {
      console.error("Campus parent portal access error:", accessError);
      setError(accessError.message || "Could not update parent portal access.");
    } finally {
      setGuardianAccessSavingKey("");
    }
  }

  if (loading && !bootstrapped) {
    return (
      <CampusInlineLoading
        title="Loading Students"
        detail="Preparing student records, demographic data, guardians, tuition status, and campus details..."
      />
    );
  }

  const totalStudents = students.length;
  const activeStudents = students.filter((student) => student.isActive).length;
  const currentlyEnrolled = students.filter(
    (student) => (student.currentEnrollmentStatus || "").toLowerCase() === "currently enrolled"
  ).length;
  const pastDueStudents = students.filter(
    (student) => (student.tuitionPaymentStatus || "").toLowerCase() === "past due"
  ).length;

  if (selectedStudent) {
    const accountRows = [
      {
        label: "Student Username",
        value: renderPill(selectedStudent.deviceLoginName),
      },
      {
        label: "Student PIN",
        value: renderPill(selectedStudent.devicePin),
      },
    ];

    const demographicRows = [
      { label: "Student Number", value: renderPill(selectedStudent.studentNumber) },
      { label: "State ID", value: renderPill(selectedStudent.stateId) },
      { label: "Local ID", value: renderPill(selectedStudent.localId) },
      { label: "Date Of Birth", value: formatDate(selectedStudent.dateOfBirth) },
      { label: "Gender", value: renderPill(selectedStudent.gender) },
      { label: "Sex", value: renderPill(selectedStudent.sex) },
      { label: "Primary Language", value: renderPill(selectedStudent.primaryLanguage) },
      { label: "Home Language", value: renderPill(selectedStudent.homeLanguage) },
      { label: "Ethnicity", value: renderPill(selectedStudent.ethnicity) },
      {
        label: "Race / Ethnicity",
        value: selectedStudent.raceEthnicity?.length
          ? selectedStudent.raceEthnicity.join(", ")
          : "Not set",
      },
    ];

    const enrollmentRows = [
      { label: "Grade Level", value: renderPill(selectedStudent.gradeLevel) },
      { label: "School", value: renderPill(selectedStudent.schoolName) },
      { label: "Campus", value: renderPill(selectedStudent.campusName) },
      { label: "Teacher", value: renderPill(selectedStudent.homeroomTeacher, "Not assigned") },
      { label: "Counselor", value: renderPill(selectedStudent.counselorName) },
      { label: "Enrollment Status", value: renderPill(selectedStudent.enrollmentStatus) },
      {
        label: "Current Status",
        value: renderPill(selectedStudent.currentEnrollmentStatus),
      },
      { label: "Start Date", value: formatDate(selectedStudent.enrollmentStartDate) },
      { label: "End Date", value: formatDate(selectedStudent.enrollmentEndDate) },
      {
        label: "Graduation Year",
        value: renderPill(
          selectedStudent.graduationYear || selectedStudent.enrollment?.graduationYear
        ),
      },
    ];
    const selectedPrimaryGuardian = getPrimaryGuardian(selectedStudent);
    const activeEditSectionLabel = EDIT_SECTION_LABELS[activeEditSection] || "Student Section";
    const selectedGrades = academicRecords.grades || [];
    const selectedReports = academicRecords.reports || [];

    return (
      <div style={styles.detailPage}>
        <div style={styles.detailTopRow}>
          <button
            type="button"
            style={styles.backButton}
            onClick={() => {
              setSelectedStudentId("");
              setEditMode(false);
              setError("");
              setNotice("");
            }}
          >
            <ArrowLeft size={16} />
            Back To Students
          </button>

          <div style={styles.topRightActions}>
            <label style={styles.inlineSearch}>
              <Search size={18} color="var(--color-primary)" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search another student by name, ID, grade, or teacher"
                style={styles.searchInput}
              />
            </label>

            {!editMode ? (
              <button type="button" style={styles.editButton} onClick={() => beginEdit("demographics")}>
                <PencilLine size={16} />
                Edit Identity
              </button>
            ) : (
              <div style={styles.editingBadge}>
                Editing {activeEditSectionLabel}
              </div>
            )}
          </div>
        </div>

        <ActionToast message={error} tone="error" onDismiss={() => setError("")} />
        <ActionToast message={!error ? notice : ""} tone="success" onDismiss={() => setNotice("")} />

        <div style={styles.studentHero}>
          <div style={styles.photoColumn}>
            <img
              src={editMode && editForm?.photoUrl ? editForm.photoUrl : selectedStudent.photoUrl}
              alt={selectedStudent.displayName}
              style={styles.studentPhoto}
            />
            {editMode && activeEditSection === "demographics" ? (
              <>
                <input
                  ref={studentPhotoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleStudentPhotoSelected}
                  style={styles.hiddenFileInput}
                />
                <button
                  type="button"
                  style={styles.photoUploadButton}
                  onClick={() => studentPhotoInputRef.current?.click()}
                  disabled={photoUploading}
                >
                  {photoUploading ? "Uploading Photo..." : "Upload Photo"}
                </button>
              </>
            ) : null}
          </div>

          <div style={styles.heroBody}>
            <div style={styles.heroKicker}>
              {selectedStudent.gradeLevel ? `Grade ${selectedStudent.gradeLevel}` : "Student Record"}
            </div>
            <h1 style={styles.heroName}>{selectedStudent.displayName}</h1>
            <div style={styles.heroMeta}>
              <span>{selectedStudent.studentNumber || "No student number"}</span>
              <span>•</span>
              <span>{selectedStudent.schoolName || "No school assigned"}</span>
              <span>•</span>
              <span>{selectedStudent.currentEnrollmentStatus || "Status not set"}</span>
            </div>
            <div style={styles.heroPills}>
              <span style={styles.heroPill}>{selectedStudent.homeroomTeacher || "No teacher selected"}</span>
              <span style={styles.heroPill}>{selectedStudent.primaryLanguage || "Language not set"}</span>
              <span style={styles.heroPill}>
                {selectedStudent.tuitionPaymentStatus || "Tuition status not set"}
              </span>
              {(selectedStudent.tags || []).map((tag) => (
                <span key={tag} style={styles.heroTag}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {!editMode ? (
          <div style={styles.sectionGrid}>
            <SectionCard
              icon={KeyRound}
              title="Account Information"
              action={renderSectionEditAction("account")}
            >
              <DetailGrid items={accountRows} />
            </SectionCard>

            <SectionCard
              icon={User}
              title="Demographics"
              action={renderSectionEditAction("demographics")}
            >
              <DetailGrid items={demographicRows} />
            </SectionCard>

            <SectionCard
              icon={GraduationCap}
              title="Enrollment"
              action={renderSectionEditAction("enrollment")}
            >
              <DetailGrid items={enrollmentRows} />
            </SectionCard>

            <SectionCard
              icon={BadgeDollarSign}
              title="Tuition"
              action={renderSectionEditAction("tuition")}
            >
              <DetailGrid
                items={[
                  {
                    label: "Payment Status",
                    value: renderPill(selectedStudent.tuitionPaymentStatus),
                  },
                  {
                    label: "Balance",
                    value: formatCurrencyFromCents(selectedStudent.tuitionBalanceCents),
                  },
                  {
                    label: "Billing Group",
                    value:
                      selectedTuitionProfile.familyBillingName ||
                      selectedTuitionProfile.familyBillingGroupId ||
                      selectedStudent.householdName ||
                      "Individual student",
                  },
                  {
                    label: "Payer",
                    value:
                      selectedTuitionProfile.payerName ||
                      selectedTuitionProfile.payerEmail ||
                      selectedPrimaryGuardian.name ||
                      selectedPrimaryGuardian.email ||
                      selectedStudent.primaryEmail ||
                      "Not set",
                  },
                  {
                    label: "Billing Started",
                    value: selectedTuitionProfile.billingStarted ? "Yes" : "Not yet",
                  },
                  {
                    label: "Stripe Sync",
                    value: selectedTuitionProfile.stripeInvoiceSync?.status || "Not sent",
                  },
                ]}
              />
              <div style={styles.tuitionSubgrid}>
                <div style={styles.tuitionPanel}>
                  <div style={styles.stackTitle}>Family Billing</div>
                  {selectedFamilyBillingStudents.length ? (
                    <div style={styles.stackList}>
                      {selectedFamilyBillingStudents.map((student) => (
                        <div key={student.id} style={styles.miniRow}>
                          <span>{student.displayName}</span>
                          <strong>{formatCurrencyFromCents(student.tuitionBalanceCents)}</strong>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={styles.emptyInline}>No linked siblings or family group members.</div>
                  )}
                </div>

                <div style={styles.tuitionPanel}>
                  <div style={styles.stackTitle}>Added Fees</div>
                  {selectedTuitionProfile.fees.length ? (
                    <div style={styles.stackList}>
                      {selectedTuitionProfile.fees.map((fee) => (
                        <div key={fee.id || `${fee.label}-${fee.date}`} style={styles.miniRow}>
                          <span>{fee.label || "Fee"} {fee.date ? `• ${formatDate(fee.date)}` : ""}</span>
                          <strong>{formatCurrencyFromCents(fee.amountCents)}</strong>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={styles.emptyInline}>No extra student fees added.</div>
                  )}
                </div>

                <div style={styles.tuitionPanel}>
                  <div style={styles.stackTitle}>Payments Made</div>
                  {selectedTuitionProfile.payments.length ? (
                    <div style={styles.stackList}>
                      {selectedTuitionProfile.payments.map((payment) => (
                        <div key={payment.id || `${payment.method}-${payment.date}`} style={styles.miniRow}>
                          <span>
                            {payment.method || "Payment"} {payment.date ? `• ${formatDate(payment.date)}` : ""}
                          </span>
                          <strong>{formatCurrencyFromCents(payment.amountCents)}</strong>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={styles.emptyInline}>No payments recorded yet.</div>
                  )}
                </div>

                <div style={styles.tuitionPanel}>
                  <div style={styles.stackTitle}>Generated Bills</div>
                  {selectedTuitionProfile.generatedBills.length ? (
                    <div style={styles.stackList}>
                      {selectedTuitionProfile.generatedBills.slice(0, 6).map((bill) => (
                        <div key={bill.id || `${bill.label}-${bill.dueDate}`} style={styles.miniRow}>
                          <span>{bill.label || "Bill"} {bill.dueDate ? `• ${formatDate(bill.dueDate)}` : ""}</span>
                          <strong>{formatCurrencyFromCents(bill.amountCents)}</strong>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={styles.emptyInline}>No generated billing schedule yet.</div>
                  )}
                </div>
              </div>
            </SectionCard>

            <SectionCard
              icon={Home}
              title="Address And Household"
              action={renderSectionEditAction("household")}
            >
              <DetailGrid
                items={[
                  { label: "Household", value: renderPill(selectedStudent.householdName) },
                  { label: "Primary Phone", value: renderPill(selectedStudent.primaryPhone) },
                  { label: "Primary Email", value: renderPill(selectedStudent.primaryEmail) },
                  {
                    label: "Street Address",
                    value: [selectedStudent.streetAddress1, selectedStudent.streetAddress2]
                      .filter(Boolean)
                      .join(", "),
                  },
                  {
                    label: "City / State / ZIP",
                    value: [selectedStudent.city, selectedStudent.state, selectedStudent.postalCode]
                      .filter(Boolean)
                      .join(", "),
                  },
                ]}
              />
            </SectionCard>

            <SectionCard
              icon={GraduationCap}
              title="Subject Teachers"
              action={renderSectionEditAction("enrollment")}
            >
              <div style={styles.stackList}>
                {getSubjectTeacherAssignments(selectedStudent).length ? (
                  getSubjectTeacherAssignments(selectedStudent).map((assignment, index) => (
                    <div key={`${assignment.subjectName || "subject"}-${index}`} style={styles.stackCard}>
                      <div style={styles.stackTitle}>{assignment.subjectName || "Subject"}</div>
                      <div style={styles.stackMeta}>
                        {assignment.teacherDisplayName || "Teacher not assigned"}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={styles.emptyInline}>No additional subject teachers assigned.</div>
                )}
              </div>
            </SectionCard>

            <SectionCard icon={BookOpen} title="Grades">
              {!academicRecords.schemaReady ? (
                <div style={styles.emptyInline}>
                  Teacher gradebook tables are not installed yet.
                </div>
              ) : academicLoading ? (
                <div style={styles.emptyInline}>Loading teacher-entered grades...</div>
              ) : selectedGrades.length ? (
                <div style={styles.stackList}>
                  {selectedGrades.slice(0, 12).map((grade) => {
                    const percentage = formatPercentage(grade.score, grade.pointsPossible);

                    return (
                      <div key={grade.id} style={styles.stackCard}>
                        <div style={styles.stackCardHeader}>
                          <div>
                            <div style={styles.stackTitle}>{grade.assignmentTitle}</div>
                            <div style={styles.stackMeta}>
                              {[grade.subjectName, grade.academicQuarter, formatDate(grade.dueDate)]
                                .filter(Boolean)
                                .join(" • ")}
                            </div>
                          </div>
                          <div style={styles.gradeScore}>
                            {formatScore(grade.score, grade.pointsPossible)}
                            {percentage ? <span>{percentage}</span> : null}
                          </div>
                        </div>
                        <div style={styles.stackCopy}>
                          {grade.feedback || `${grade.teacherName || "Teacher"} has not added feedback yet.`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={styles.emptyInline}>
                  No teacher-entered grades have been posted for this student yet.
                </div>
              )}
            </SectionCard>

            <SectionCard icon={FileText} title="Report Cards">
              {!academicRecords.schemaReady ? (
                <div style={styles.emptyInline}>
                  Teacher report-card tables are not installed yet.
                </div>
              ) : academicLoading ? (
                <div style={styles.emptyInline}>Loading report cards...</div>
              ) : selectedReports.length ? (
                <div style={styles.stackList}>
                  {selectedReports.map((report) => (
                    <div key={report.id} style={styles.stackCard}>
                      <div style={styles.stackCardHeader}>
                        <div>
                          <div style={styles.stackTitle}>
                            {report.title || `${report.academicQuarter} Report Card`}
                          </div>
                          <div style={styles.stackMeta}>
                            {[report.academicQuarter, report.teacherName, report.status]
                              .filter(Boolean)
                              .join(" • ")}
                          </div>
                        </div>
                        <div style={report.status === "posted" ? styles.portalBadgeActive : styles.portalBadgeMuted}>
                          {report.status || "draft"}
                        </div>
                      </div>
                      <div style={styles.stackCopy}>
                        {report.summary || report.academicProgress || "No narrative summary added yet."}
                      </div>
                      {Array.isArray(report.gradeSnapshot) && report.gradeSnapshot.length ? (
                        <div style={styles.reportSnapshotGrid}>
                          {report.gradeSnapshot.slice(0, 4).map((subject) => (
                            <div key={subject.subjectId || subject.subjectName} style={styles.reportSnapshotItem}>
                              <span>{subject.subjectName || "Subject"}</span>
                              <strong>{subject.fullGrade || subject.percentage || "0%"}</strong>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyInline}>
                  No teacher-created report cards have been saved for this student yet.
                </div>
              )}
            </SectionCard>

            <SectionCard
              icon={Users}
              title="Guardians"
              action={renderSectionEditAction("guardians")}
            >
              <div style={styles.stackList}>
                {selectedStudent.guardians.length ? (
                  selectedStudent.guardians.map((guardian, index) => (
                    <div key={`${guardian.name}-${index}`} style={styles.stackCard}>
                      <div style={styles.stackCardHeader}>
                        <div style={styles.stackTitle}>{guardian.name || "Unnamed Guardian"}</div>
                        <div style={guardian.portalAccess ? styles.portalBadgeActive : styles.portalBadgeMuted}>
                          {guardian.portalAccess ? "Parent Portal On" : "Parent Portal Off"}
                        </div>
                      </div>
                      <div style={styles.stackMeta}>
                        {guardian.relationship || "Relationship not set"}
                      </div>
                      <div style={styles.stackCopy}>
                        {guardian.phone || "No phone"} {guardian.email ? `• ${guardian.email}` : ""}
                      </div>
                      {guardian.linkedUserId ? (
                        <div style={styles.stackHint}>Linked account ready for `/parent`.</div>
                      ) : (
                        <div style={styles.stackHint}>Invite this guardian to connect their parent account.</div>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={styles.emptyInline}>No guardians added.</div>
                )}
              </div>
            </SectionCard>

            <SectionCard
              icon={Phone}
              title="Emergency Contacts"
              action={renderSectionEditAction("emergency")}
            >
              <div style={styles.stackList}>
                {selectedStudent.emergencyContacts.length ? (
                  selectedStudent.emergencyContacts.map((contact, index) => (
                    <div key={`${contact.name}-${index}`} style={styles.stackCard}>
                      <div style={styles.stackTitle}>{contact.name || "Unnamed Contact"}</div>
                      <div style={styles.stackMeta}>
                        {contact.relationship || "Relationship not set"}
                      </div>
                      <div style={styles.stackCopy}>
                        {contact.phone || "No phone"}
                        {contact.priority ? ` • Priority ${contact.priority}` : ""}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={styles.emptyInline}>No emergency contacts added.</div>
                )}
              </div>
            </SectionCard>

            <SectionCard
              icon={HeartPulse}
              title="Medical"
              action={renderSectionEditAction("medical")}
            >
              <DetailGrid
                items={[
                  {
                    label: "Physician",
                    value: renderPill(selectedStudent.medical?.physician),
                  },
                  {
                    label: "Immunization Status",
                    value: renderPill(selectedStudent.medical?.immunizationStatus),
                  },
                  {
                    label: "Allergies",
                    value: Array.isArray(selectedStudent.medical?.allergies) &&
                      selectedStudent.medical.allergies.length
                      ? selectedStudent.medical.allergies.join(", ")
                      : "None listed",
                  },
                  {
                    label: "Medications",
                    value: Array.isArray(selectedStudent.medical?.medications) &&
                      selectedStudent.medical.medications.length
                      ? selectedStudent.medical.medications.join(", ")
                      : "None listed",
                  },
                  {
                    label: "Health Alerts",
                    value: Array.isArray(selectedStudent.medical?.healthAlerts) &&
                      selectedStudent.medical.healthAlerts.length
                      ? selectedStudent.medical.healthAlerts.join(", ")
                      : "None listed",
                  },
                ]}
              />
            </SectionCard>

            <SectionCard
              icon={ShieldAlert}
              title="Disciplinary"
              action={renderSectionEditAction("disciplinary")}
            >
              <div style={styles.stackList}>
                {selectedStudent.disciplinary?.length ? (
                  selectedStudent.disciplinary.map((entry, index) => (
                    <div key={`disciplinary-${index}`} style={styles.stackCard}>
                      <div style={styles.stackTitle}>
                        {entry.incidentType || "Incident"} {entry.date ? `• ${formatDate(entry.date)}` : ""}
                      </div>
                      <div style={styles.stackMeta}>
                        {entry.actionTaken || "No action recorded"}
                      </div>
                      <div style={styles.stackCopy}>
                        {entry.staffName || "No staff listed"} {entry.notes ? `• ${entry.notes}` : ""}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={styles.emptyInline}>No disciplinary history recorded.</div>
                )}
              </div>
            </SectionCard>

            <SectionCard
              icon={Phone}
              title="Parent Contact Log"
              action={renderSectionEditAction("parentContact")}
            >
              <div style={styles.stackList}>
                {selectedStudent.parentContactLog?.length ? (
                  selectedStudent.parentContactLog.map((entry, index) => (
                    <div key={`contact-log-${index}`} style={styles.stackCard}>
                      <div style={styles.stackTitle}>
                        {entry.contactName || "Parent Contact"} {entry.date ? `• ${formatDate(entry.date)}` : ""}
                      </div>
                      <div style={styles.stackMeta}>
                        {entry.method || "Method not set"} {entry.staffName ? `• ${entry.staffName}` : ""}
                      </div>
                      <div style={styles.stackCopy}>{entry.summary || "No summary recorded."}</div>
                    </div>
                  ))
                ) : (
                  <div style={styles.emptyInline}>No parent contact history recorded.</div>
                )}
              </div>
            </SectionCard>

            <SectionCard
              icon={ShieldAlert}
              title="Programs And Compliance"
              action={renderSectionEditAction("programs")}
            >
              <DetailGrid
                items={[
                  {
                    label: "IEP",
                    value: selectedStudent.programParticipation?.iep ? "Yes" : "No",
                  },
                  {
                    label: "504",
                    value: selectedStudent.programParticipation?.section504 ? "Yes" : "No",
                  },
                  {
                    label: "ELL",
                    value: selectedStudent.programParticipation?.ell ? "Yes" : "No",
                  },
                  {
                    label: "Gifted / Talented",
                    value: selectedStudent.programParticipation?.giftedTalented ? "Yes" : "No",
                  },
                  {
                    label: "Migrant",
                    value: selectedStudent.programParticipation?.migrant ? "Yes" : "No",
                  },
                  {
                    label: "Foster Care",
                    value: selectedStudent.programParticipation?.fosterCare ? "Yes" : "No",
                  },
                  {
                    label: "Homeless",
                    value: selectedStudent.programParticipation?.homeless ? "Yes" : "No",
                  },
                ]}
              />
            </SectionCard>

            <SectionCard
              icon={Languages}
              title="Additional Data"
              action={renderSectionEditAction("programs")}
            >
              <div style={styles.additionalList}>
                {renderKeyValues(selectedStudent.demographics).map(([key, value]) => (
                  <div key={key} style={styles.additionalRow}>
                    <div style={styles.additionalLabel}>{key}</div>
                    <div style={styles.additionalValue}>
                      {Array.isArray(value) ? value.join(", ") : String(value)}
                    </div>
                  </div>
                ))}
                {renderKeyValues(selectedStudent.customFields).map(([key, value]) => (
                  <div key={key} style={styles.additionalRow}>
                    <div style={styles.additionalLabel}>{key}</div>
                    <div style={styles.additionalValue}>
                      {Array.isArray(value) ? value.join(", ") : String(value)}
                    </div>
                  </div>
                ))}
                {renderKeyValues(selectedStudent.demographics).length === 0 &&
                renderKeyValues(selectedStudent.customFields).length === 0 ? (
                  <div style={styles.emptyInline}>No additional details recorded.</div>
                ) : null}
              </div>
            </SectionCard>
          </div>
        ) : (
          <div style={styles.editGrid}>
            <div style={styles.editSaveBar}>
              <div style={styles.editSaveBarText}>
                <span style={styles.editSaveBarKicker}>Editing section</span>
                <strong>{activeEditSectionLabel}</strong>
              </div>
              <div style={styles.editActionRow}>
                <button type="button" style={styles.cancelButton} onClick={cancelEdit}>
                  <X size={16} />
                  Cancel
                </button>
                <button type="button" style={styles.saveButton} onClick={saveStudent} disabled={saving}>
                  <Save size={16} />
                  {saving ? "Saving..." : "Save Section"}
                </button>
              </div>
            </div>

            {activeEditSection === "account" ? (
            <SectionCard
              icon={KeyRound}
              title="Account Information"
              sectionRef={editSectionRefs.account}
            >
              <div style={styles.formGrid}>
                <LabeledInput
                  label="Student Username"
                  value={editForm.deviceLoginName || ""}
                  onChange={(value) => updateForm("deviceLoginName", normalizeGeneratedStudentLoginName(value))}
                  placeholder="Used for student devices and hall pass"
                />
                <LabeledInput
                  label="Student PIN"
                  value={editForm.devicePin || ""}
                  onChange={(value) => updateForm("devicePin", value.replace(/\D+/g, "").slice(0, 4))}
                  placeholder="0316"
                />
              </div>
              <div style={styles.fieldHint}>
                This account is shared with Oikos Managed Devices and Hall Pass.
              </div>
            </SectionCard>
            ) : null}

            {activeEditSection === "demographics" ? (
            <SectionCard
              icon={User}
              title="Identity And Demographics"
              sectionRef={editSectionRefs.demographics}
            >
              <div style={styles.formGrid}>
                <LabeledInput label="Display Name" value={editForm.displayName} onChange={(value) => updateForm("displayName", value)} />
                <LabeledInput label="Legal First Name" value={editForm.legalFirstName} onChange={(value) => updateForm("legalFirstName", value)} />
                <LabeledInput label="Legal Middle Name" value={editForm.legalMiddleName} onChange={(value) => updateForm("legalMiddleName", value)} />
                <LabeledInput label="Legal Last Name" value={editForm.legalLastName} onChange={(value) => updateForm("legalLastName", value)} />
                <LabeledInput label="Legal Suffix" value={editForm.legalSuffix} onChange={(value) => updateForm("legalSuffix", value)} />
                <LabeledInput label="Preferred First Name" value={editForm.preferredFirstName} onChange={(value) => updateForm("preferredFirstName", value)} />
                <LabeledInput label="Preferred Last Name" value={editForm.preferredLastName} onChange={(value) => updateForm("preferredLastName", value)} />
                <LabeledInput label="Student Number" value={editForm.studentNumber} onChange={(value) => updateForm("studentNumber", value)} />
                <LabeledInput label="State ID" value={editForm.stateId} onChange={(value) => updateForm("stateId", value)} />
                <LabeledInput label="Local ID" value={editForm.localId} onChange={(value) => updateForm("localId", value)} />
                <LabeledInput label="Campus Person ID" value={editForm.campusPersonId} onChange={(value) => updateForm("campusPersonId", value)} />
                <LabeledInput label="Date Of Birth" type="date" value={editForm.dateOfBirth || ""} onChange={(value) => updateForm("dateOfBirth", value)} />
                <LabeledInput label="Gender" value={editForm.gender} onChange={(value) => updateForm("gender", value)} />
                <LabeledInput label="Sex" value={editForm.sex} onChange={(value) => updateForm("sex", value)} />
                <LabeledInput label="Primary Language" value={editForm.primaryLanguage} onChange={(value) => updateForm("primaryLanguage", value)} />
                <LabeledInput label="Home Language" value={editForm.homeLanguage} onChange={(value) => updateForm("homeLanguage", value)} />
                <LabeledInput label="Ethnicity" value={editForm.ethnicity} onChange={(value) => updateForm("ethnicity", value)} />
                <LabeledInput label="Race / Ethnicity" value={editForm.raceEthnicityText} onChange={(value) => updateForm("raceEthnicityText", value)} placeholder="Comma separated" />
                <LabeledInput label="Tags" value={editForm.tagsText} onChange={(value) => updateForm("tagsText", value)} placeholder="Comma separated tags" />
              </div>
              <div style={styles.fieldHint}>Use the upload button beside the student photo to choose a profile image.</div>
            </SectionCard>
            ) : null}

            {activeEditSection === "enrollment" ? (
            <SectionCard
              icon={GraduationCap}
              title="Enrollment And Tuition"
              sectionRef={editSectionRefs.enrollment}
            >
              <div style={styles.formGrid}>
                <LabeledInput label="Grade Level" value={editForm.gradeLevel} onChange={(value) => updateForm("gradeLevel", value)} />
                <LabeledInput label="School Name" value={editForm.schoolName} onChange={(value) => updateForm("schoolName", value)} />
                <LabeledInput label="Campus Name" value={editForm.campusName} onChange={(value) => updateForm("campusName", value)} />
                <LabeledSelect
                  label="Teacher"
                  value={editForm.homeroomTeacher}
                  onChange={(value) => updateForm("homeroomTeacher", value)}
                  options={teacherOptions}
                  placeholder={teacherOptions.length ? "Select teacher" : "No teachers available"}
                />
                <LabeledInput label="Counselor" value={editForm.counselorName} onChange={(value) => updateForm("counselorName", value)} />
                <LabeledInput label="Enrollment Status" value={editForm.enrollmentStatus} onChange={(value) => updateForm("enrollmentStatus", value)} />
                <LabeledInput label="Current Enrollment Status" value={editForm.currentEnrollmentStatus} onChange={(value) => updateForm("currentEnrollmentStatus", value)} />
                <LabeledInput label="Enrollment Start Date" type="date" value={editForm.enrollmentStartDate || ""} onChange={(value) => updateForm("enrollmentStartDate", value)} />
                <LabeledInput label="Enrollment End Date" type="date" value={editForm.enrollmentEndDate || ""} onChange={(value) => updateForm("enrollmentEndDate", value)} />
                <LabeledInput label="Graduation Year" type="number" value={editForm.graduationYear || ""} onChange={(value) => updateForm("graduationYear", value)} />
              </div>
              <div style={styles.stackEditList}>
                <div style={styles.fieldHint}>
                  Add additional teachers by subject so they can manage their own grades and report notes for this student.
                </div>
                {getSubjectTeacherAssignments(editForm).map((assignment, index) => (
                  <div key={`subject-teacher-${index}`} style={styles.stackEditCard}>
                    <div style={styles.stackEditHeader}>
                      <div style={styles.stackEditTitle}>Subject Teacher {index + 1}</div>
                      <button
                        type="button"
                        style={styles.removeMiniButton}
                        onClick={() => removeSubjectTeacherAssignment(index)}
                      >
                        Remove
                      </button>
                    </div>
                    <div style={styles.formGrid}>
                      <LabeledInput
                        label="Subject"
                        value={assignment.subjectName || ""}
                        onChange={(value) => updateSubjectTeacherAssignment(index, "subjectName", value)}
                        placeholder="Music, Art, Bible"
                      />
                      <LabeledSelect
                        label="Teacher"
                        value={assignment.teacherStaffId || ""}
                        onChange={(value) => updateSubjectTeacherAssignment(index, "teacherStaffId", value)}
                        options={teacherStaffOptions}
                        placeholder={teacherStaffOptions.length ? "Select teacher" : "No teachers available"}
                      />
                    </div>
                  </div>
                ))}
                <button type="button" style={styles.addMiniButton} onClick={addSubjectTeacherAssignment}>
                  Add Subject Teacher
                </button>
              </div>
            </SectionCard>
            ) : null}

            {activeEditSection === "tuition" ? (
            <SectionCard
              icon={BadgeDollarSign}
              title="Tuition Billing"
              sectionRef={editSectionRefs.tuition}
            >
              <div style={styles.formGrid}>
                <LabeledInput label="Tuition Payment Status" value={editForm.tuitionPaymentStatus} onChange={(value) => updateForm("tuitionPaymentStatus", value)} />
                <MoneyInput label="Tuition Balance" valueCents={editForm.tuitionBalanceCents || 0} onChangeCents={(amountCents) => updateForm("tuitionBalanceCents", amountCents)} />
                <LabeledInput
                  label="Family Billing Group ID"
                  value={getTuitionProfile(editForm).familyBillingGroupId}
                  onChange={(value) => updateTuitionProfileField("familyBillingGroupId", value)}
                  placeholder="Example: smith-family"
                />
                <LabeledInput
                  label="Family Billing Name"
                  value={getTuitionProfile(editForm).familyBillingName}
                  onChange={(value) => updateTuitionProfileField("familyBillingName", value)}
                  placeholder="Smith Family"
                />
                <LabeledInput
                  label="Payer Name"
                  value={getTuitionProfile(editForm).payerName}
                  onChange={(value) => updateTuitionProfileField("payerName", value)}
                />
                <LabeledInput
                  label="Payer Email"
                  type="email"
                  value={getTuitionProfile(editForm).payerEmail}
                  onChange={(value) => updateTuitionProfileField("payerEmail", value)}
                />
                <LabeledInput
                  label="Payer Phone"
                  value={getTuitionProfile(editForm).payerPhone}
                  onChange={(value) => updateTuitionProfileField("payerPhone", value)}
                />
                <LabeledTextarea
                  label="Tuition Notes"
                  value={getTuitionProfile(editForm).notes}
                  onChange={(value) => updateTuitionProfileField("notes", value)}
                  rows={3}
                />
              </div>
              <button type="button" style={styles.addMiniButton} onClick={useHouseholdForTuitionBilling}>
                Use Household And Primary Guardian For Billing
              </button>

              <div style={styles.stackEditList}>
                <div style={styles.stackEditHeader}>
                  <div>
                    <div style={styles.stackEditTitle}>Student Fees</div>
                    <div style={styles.fieldHint}>Add student-specific charges that should be visible from this record.</div>
                  </div>
                  <button type="button" style={styles.addMiniButton} onClick={addTuitionFee}>
                    Add Fee
                  </button>
                </div>
                {getTuitionProfile(editForm).fees.map((fee, index) => (
                  <div key={fee.id || `fee-${index}`} style={styles.stackEditCard}>
                    <div style={styles.stackEditHeader}>
                      <div style={styles.stackEditTitle}>Fee {index + 1}</div>
                      <button type="button" style={styles.removeMiniButton} onClick={() => removeTuitionFee(index)}>
                        Remove
                      </button>
                    </div>
                    <div style={styles.formGrid}>
                      <LabeledInput label="Date" type="date" value={fee.date || ""} onChange={(value) => updateTuitionFee(index, { date: value })} />
                      <LabeledInput label="Label" value={fee.label || ""} onChange={(value) => updateTuitionFee(index, { label: value })} />
                      <MoneyInput label="Amount" valueCents={fee.amountCents} onChangeCents={(amountCents) => updateTuitionFee(index, { amountCents })} />
                      <LabeledInput label="Status" value={fee.status || "open"} onChange={(value) => updateTuitionFee(index, { status: value })} />
                      <LabeledTextarea label="Notes" rows={2} value={fee.notes || ""} onChange={(value) => updateTuitionFee(index, { notes: value })} />
                    </div>
                  </div>
                ))}
                {!getTuitionProfile(editForm).fees.length ? (
                  <div style={styles.emptyInline}>No student-specific fees yet.</div>
                ) : null}
              </div>

              <div style={styles.stackEditList}>
                <div style={styles.stackEditHeader}>
                  <div>
                    <div style={styles.stackEditTitle}>Payments Made</div>
                    <div style={styles.fieldHint}>Record cash, check, Stripe, scholarship, or adjustment payments.</div>
                  </div>
                  <button type="button" style={styles.addMiniButton} onClick={addTuitionPayment}>
                    Add Payment
                  </button>
                </div>
                {getTuitionProfile(editForm).payments.map((payment, index) => (
                  <div key={payment.id || `payment-${index}`} style={styles.stackEditCard}>
                    <div style={styles.stackEditHeader}>
                      <div style={styles.stackEditTitle}>Payment {index + 1}</div>
                      <button type="button" style={styles.removeMiniButton} onClick={() => removeTuitionPayment(index)}>
                        Remove
                      </button>
                    </div>
                    <div style={styles.formGrid}>
                      <LabeledInput label="Date" type="date" value={payment.date || ""} onChange={(value) => updateTuitionPayment(index, { date: value })} />
                      <LabeledInput label="Method" value={payment.method || ""} onChange={(value) => updateTuitionPayment(index, { method: value })} placeholder="Stripe, check, cash" />
                      <MoneyInput label="Amount" valueCents={payment.amountCents} onChangeCents={(amountCents) => updateTuitionPayment(index, { amountCents })} />
                      <LabeledInput label="Reference" value={payment.reference || ""} onChange={(value) => updateTuitionPayment(index, { reference: value })} />
                      <LabeledTextarea label="Notes" rows={2} value={payment.notes || ""} onChange={(value) => updateTuitionPayment(index, { notes: value })} />
                    </div>
                  </div>
                ))}
                {!getTuitionProfile(editForm).payments.length ? (
                  <div style={styles.emptyInline}>No payments recorded yet.</div>
                ) : null}
              </div>

              <div style={styles.stackEditList}>
                <div style={styles.stackEditHeader}>
                  <div>
                    <div style={styles.stackEditTitle}>Invoices And Bills</div>
                    <div style={styles.fieldHint}>Mark invoices paid here when a family pays by cash or check.</div>
                  </div>
                </div>
                {getTuitionProfile(editForm).generatedBills.map((bill, index) => (
                  <div key={bill.id || `bill-${index}`} style={styles.stackEditCard}>
                    <div style={styles.stackEditHeader}>
                      <div>
                        <div style={styles.stackEditTitle}>{bill.label || `Bill ${index + 1}`}</div>
                        <div style={styles.fieldHint}>
                          Due {bill.dueDate ? formatDate(bill.dueDate) : "not set"} • {formatCurrencyFromCents(bill.amountCents)}
                        </div>
                      </div>
                      <div style={styles.inlineButtonRow}>
                        <button type="button" style={styles.addMiniButton} onClick={() => markTuitionBillPaid(index, "Cash")}>
                          Paid Cash
                        </button>
                        <button type="button" style={styles.addMiniButton} onClick={() => markTuitionBillPaid(index, "Check")}>
                          Paid Check
                        </button>
                      </div>
                    </div>
                    <div style={styles.formGrid}>
                      <LabeledInput label="Status" value={bill.status || "open"} onChange={(value) => updateTuitionBill(index, { status: value })} />
                      <MoneyInput label="Paid Amount" valueCents={bill.paidAmountCents || 0} onChangeCents={(amountCents) => updateTuitionBill(index, { paidAmountCents: amountCents, remainingAmountCents: Math.max(0, Number(bill.amountCents || 0) - amountCents) })} />
                      <LabeledInput label="Payment Method" value={bill.paymentMethod || ""} onChange={(value) => updateTuitionBill(index, { paymentMethod: value })} />
                      <LabeledInput label="Paid Date" type="date" value={bill.paidDate || ""} onChange={(value) => updateTuitionBill(index, { paidDate: value })} />
                    </div>
                  </div>
                ))}
                {!getTuitionProfile(editForm).generatedBills.length ? (
                  <div style={styles.emptyInline}>No generated invoices or bills yet.</div>
                ) : null}
              </div>
            </SectionCard>
            ) : null}

            {activeEditSection === "household" ? (
            <SectionCard
              icon={Home}
              title="Household And Contacts"
              sectionRef={editSectionRefs.household}
            >
              <div style={styles.formGrid}>
                <LabeledInput label="Household Name" value={editForm.householdName} onChange={(value) => updateForm("householdName", value)} />
                <LabeledInput label="Primary Phone" value={editForm.primaryPhone} onChange={(value) => updateForm("primaryPhone", value)} />
                <LabeledInput label="Primary Email" value={editForm.primaryEmail} onChange={(value) => updateForm("primaryEmail", value)} />
                <LabeledInput label="Street Address 1" value={editForm.streetAddress1} onChange={(value) => updateForm("streetAddress1", value)} />
                <LabeledInput label="Street Address 2" value={editForm.streetAddress2} onChange={(value) => updateForm("streetAddress2", value)} />
                <LabeledInput label="City" value={editForm.city} onChange={(value) => updateForm("city", value)} />
                <LabeledInput label="State" value={editForm.state} onChange={(value) => updateForm("state", value)} />
                <LabeledInput label="Postal Code" value={editForm.postalCode} onChange={(value) => updateForm("postalCode", value)} />
              </div>
            </SectionCard>
            ) : null}

            {activeEditSection === "guardians" ? (
            <SectionCard
              icon={Users}
              title="Guardians"
              sectionRef={editSectionRefs.guardians}
            >
              <div style={styles.stackEditList}>
                {reusableGuardianOptions.length ? (
                  <div style={styles.guardianLinkPanel}>
                    <div style={styles.guardianLinkMeta}>
                      <div style={styles.guardianAccessTitle}>Link Existing Guardian</div>
                      <div style={styles.guardianAccessCopy}>
                        Reuse a guardian already attached to a sibling or another student in your school.
                      </div>
                    </div>
                    <div style={styles.guardianLinkControls}>
                      <select
                        value={guardianLinkSelection}
                        onChange={(event) => setGuardianLinkSelection(event.target.value)}
                        style={styles.input}
                      >
                        <option value="">Select a guardian from another student</option>
                        {reusableGuardianOptions.map((option) => (
                          <option key={option.key} value={option.key}>
                            {option.label} - {option.description}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        style={styles.inlineSecondaryButton}
                        onClick={handleLinkExistingGuardian}
                        disabled={!guardianLinkSelection}
                      >
                        Add Linked Guardian
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={styles.fieldHint}>
                    Once another sibling or student has a guardian on file, you can link that guardian here.
                  </div>
                )}
                {(editForm.guardians || []).map((guardian, index) => (
                  <div key={`guardian-${index}`} style={styles.stackEditCard}>
                    <div style={styles.stackEditHeader}>
                      <div style={styles.stackEditTitle}>Guardian {index + 1}</div>
                      <button
                        type="button"
                        style={styles.removeMiniButton}
                        onClick={() => removeListItem("guardians", index)}
                      >
                        Remove
                      </button>
                    </div>
                    <div style={styles.formGrid}>
                      <LabeledInput label="Name" value={guardian.name || ""} onChange={(value) => updateListItem("guardians", index, "name", value)} />
                      <LabeledInput label="Relationship" value={guardian.relationship || ""} onChange={(value) => updateListItem("guardians", index, "relationship", value)} />
                      <LabeledInput label="Phone" value={guardian.phone || ""} onChange={(value) => updateListItem("guardians", index, "phone", value)} />
                      <LabeledInput label="Email" value={guardian.email || ""} onChange={(value) => updateListItem("guardians", index, "email", value)} />
                    </div>
                    <div style={styles.guardianAccessRow}>
                      <div style={styles.guardianAccessMeta}>
                        <div style={styles.guardianAccessTitle}>Parent Portal Access</div>
                        <div style={styles.guardianAccessCopy}>
                          {guardian.linkedUserId
                            ? guardian.portalAccess
                              ? "This parent can sign in at /parent."
                              : "Linked account found. Turn on portal access when you are ready."
                            : "Send the invite first to create or link the parent account."}
                        </div>
                      </div>
                      <div style={styles.guardianAccessActions}>
                        <button
                          type="button"
                          style={styles.inlineSecondaryButton}
                          onClick={() => handleGuardianInvite(index)}
                          disabled={guardianAccessSavingKey === `invite:${index}` || !guardian.email}
                        >
                          {guardianAccessSavingKey === `invite:${index}` ? "Sending..." : "Invite Parent"}
                        </button>
                        <button
                          type="button"
                          style={guardian.portalAccess ? styles.inlineGhostButton : styles.inlinePrimaryButton}
                          onClick={() => handleGuardianPortalAccess(index, !guardian.portalAccess)}
                          disabled={guardianAccessSavingKey === `access:${index}` || !guardian.email}
                        >
                          {guardianAccessSavingKey === `access:${index}`
                            ? "Saving..."
                            : guardian.portalAccess
                              ? "Remove Access"
                              : "Enable Access"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                <button type="button" style={styles.addMiniButton} onClick={() => addListItem("guardians", createGuardian)}>
                  Add Guardian
                </button>
              </div>
            </SectionCard>
            ) : null}

            {activeEditSection === "emergency" ? (
            <SectionCard
              icon={Phone}
              title="Emergency Contacts"
              sectionRef={editSectionRefs.emergency}
            >
              <div style={styles.stackEditList}>
                {reusableEmergencyContactOptions.length ? (
                  <div style={styles.guardianLinkPanel}>
                    <div style={styles.guardianLinkMeta}>
                      <div style={styles.guardianAccessTitle}>Link Existing Emergency Contact</div>
                      <div style={styles.guardianAccessCopy}>
                        Reuse an emergency contact already attached to a sibling or another student in your school.
                      </div>
                    </div>
                    <div style={styles.guardianLinkControls}>
                      <select
                        value={emergencyContactLinkSelection}
                        onChange={(event) => setEmergencyContactLinkSelection(event.target.value)}
                        style={styles.input}
                      >
                        <option value="">Select an emergency contact from another student</option>
                        {reusableEmergencyContactOptions.map((option) => (
                          <option key={option.key} value={option.key}>
                            {option.label} - {option.description}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        style={styles.inlineSecondaryButton}
                        onClick={handleLinkExistingEmergencyContact}
                        disabled={!emergencyContactLinkSelection}
                      >
                        Add Linked Contact
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={styles.fieldHint}>
                    Once another sibling or student has an emergency contact on file, you can link that contact here.
                  </div>
                )}
                {(editForm.emergencyContacts || []).map((contact, index) => (
                  <div key={`emergency-${index}`} style={styles.stackEditCard}>
                    <div style={styles.stackEditHeader}>
                      <div style={styles.stackEditTitle}>Emergency Contact {index + 1}</div>
                      <button
                        type="button"
                        style={styles.removeMiniButton}
                        onClick={() => removeListItem("emergencyContacts", index)}
                      >
                        Remove
                      </button>
                    </div>
                    <div style={styles.formGrid}>
                      <LabeledInput label="Name" value={contact.name || ""} onChange={(value) => updateListItem("emergencyContacts", index, "name", value)} />
                      <LabeledInput label="Relationship" value={contact.relationship || ""} onChange={(value) => updateListItem("emergencyContacts", index, "relationship", value)} />
                      <LabeledInput label="Phone" value={contact.phone || ""} onChange={(value) => updateListItem("emergencyContacts", index, "phone", value)} />
                      <LabeledInput label="Priority" value={contact.priority || ""} onChange={(value) => updateListItem("emergencyContacts", index, "priority", value)} />
                    </div>
                  </div>
                ))}
                <button type="button" style={styles.addMiniButton} onClick={() => addListItem("emergencyContacts", createEmergencyContact)}>
                  Add Emergency Contact
                </button>
              </div>
            </SectionCard>
            ) : null}

            {activeEditSection === "medical" ? (
            <SectionCard
              icon={HeartPulse}
              title="Medical"
              sectionRef={editSectionRefs.medical}
            >
              <div style={styles.formGrid}>
                <LabeledInput label="Physician" value={editForm.medical?.physician || ""} onChange={(value) => updateNestedSection("medical", "physician", value)} />
                <LabeledInput label="Immunization Status" value={editForm.medical?.immunizationStatus || ""} onChange={(value) => updateNestedSection("medical", "immunizationStatus", value)} />
                <LabeledTextarea label="Allergies" value={Array.isArray(editForm.medical?.allergies) ? editForm.medical.allergies.join(", ") : ""} onChange={(value) => updateNestedSection("medical", "allergies", value.split(",").map((item) => item.trim()).filter(Boolean))} rows={3} />
                <LabeledTextarea label="Medications" value={Array.isArray(editForm.medical?.medications) ? editForm.medical.medications.join(", ") : ""} onChange={(value) => updateNestedSection("medical", "medications", value.split(",").map((item) => item.trim()).filter(Boolean))} rows={3} />
                <LabeledTextarea label="Health Alerts" value={Array.isArray(editForm.medical?.healthAlerts) ? editForm.medical.healthAlerts.join(", ") : ""} onChange={(value) => updateNestedSection("medical", "healthAlerts", value.split(",").map((item) => item.trim()).filter(Boolean))} rows={3} />
              </div>
            </SectionCard>
            ) : null}

            {activeEditSection === "disciplinary" ? (
            <SectionCard
              icon={ShieldAlert}
              title="Disciplinary"
              sectionRef={editSectionRefs.disciplinary}
            >
              <div style={styles.stackEditList}>
                {(editForm.disciplinary || []).map((entry, index) => (
                  <div key={`disciplinary-edit-${index}`} style={styles.stackEditCard}>
                    <div style={styles.stackEditHeader}>
                      <div style={styles.stackEditTitle}>Incident {index + 1}</div>
                      <button
                        type="button"
                        style={styles.removeMiniButton}
                        onClick={() => removeListItem("disciplinary", index)}
                      >
                        Remove
                      </button>
                    </div>
                    <div style={styles.formGrid}>
                      <LabeledInput label="Date" type="date" value={entry.date || ""} onChange={(value) => updateListItem("disciplinary", index, "date", value)} />
                      <LabeledInput label="Incident Type" value={entry.incidentType || ""} onChange={(value) => updateListItem("disciplinary", index, "incidentType", value)} />
                      <LabeledInput label="Action Taken" value={entry.actionTaken || ""} onChange={(value) => updateListItem("disciplinary", index, "actionTaken", value)} />
                      <LabeledInput label="Staff Name" value={entry.staffName || ""} onChange={(value) => updateListItem("disciplinary", index, "staffName", value)} />
                      <LabeledTextarea label="Notes" value={entry.notes || ""} onChange={(value) => updateListItem("disciplinary", index, "notes", value)} rows={3} />
                    </div>
                  </div>
                ))}
                <button type="button" style={styles.addMiniButton} onClick={() => addListItem("disciplinary", createDisciplinaryEntry)}>
                  Add Discipline Entry
                </button>
              </div>
            </SectionCard>
            ) : null}

            {activeEditSection === "parentContact" ? (
            <SectionCard
              icon={Phone}
              title="Parent Contact Log"
              sectionRef={editSectionRefs.parentContact}
            >
              <div style={styles.stackEditList}>
                {(editForm.parentContactLog || []).map((entry, index) => (
                  <div key={`parent-contact-${index}`} style={styles.stackEditCard}>
                    <div style={styles.stackEditHeader}>
                      <div style={styles.stackEditTitle}>Contact Log {index + 1}</div>
                      <button
                        type="button"
                        style={styles.removeMiniButton}
                        onClick={() => removeListItem("parentContactLog", index)}
                      >
                        Remove
                      </button>
                    </div>
                    <div style={styles.formGrid}>
                      <LabeledInput label="Date" type="date" value={entry.date || ""} onChange={(value) => updateListItem("parentContactLog", index, "date", value)} />
                      <LabeledInput label="Method" value={entry.method || ""} onChange={(value) => updateListItem("parentContactLog", index, "method", value)} />
                      <LabeledInput label="Parent / Guardian" value={entry.contactName || ""} onChange={(value) => updateListItem("parentContactLog", index, "contactName", value)} />
                      <LabeledInput label="Staff Name" value={entry.staffName || ""} onChange={(value) => updateListItem("parentContactLog", index, "staffName", value)} />
                      <LabeledTextarea label="Summary" value={entry.summary || ""} onChange={(value) => updateListItem("parentContactLog", index, "summary", value)} rows={3} />
                    </div>
                  </div>
                ))}
                <button type="button" style={styles.addMiniButton} onClick={() => addListItem("parentContactLog", createParentContactEntry)}>
                  Add Contact Log Entry
                </button>
              </div>
            </SectionCard>
            ) : null}

            {activeEditSection === "programs" ? (
            <SectionCard
              icon={ShieldAlert}
              title="Programs And Extra Details"
              sectionRef={editSectionRefs.programs}
            >
              <div style={styles.formGrid}>
                <LabeledInput label="Birth Country" value={editForm.demographics?.birthCountry || ""} onChange={(value) => updateNestedSection("demographics", "birthCountry", value)} />
                <LabeledInput label="Birth City" value={editForm.demographics?.birthCity || ""} onChange={(value) => updateNestedSection("demographics", "birthCity", value)} />
                <LabeledInput label="Residency Status" value={editForm.demographics?.residencyStatus || ""} onChange={(value) => updateNestedSection("demographics", "residencyStatus", value)} />
                <LabeledInput label="Transportation" value={editForm.demographics?.transportation || ""} onChange={(value) => updateNestedSection("demographics", "transportation", value)} />
                <LabeledInput label="Schedule Type" value={editForm.enrollment?.scheduleType || ""} onChange={(value) => updateNestedSection("enrollment", "scheduleType", value)} />
                <LabeledInput label="Lunch Status" value={editForm.enrollment?.lunchStatus || ""} onChange={(value) => updateNestedSection("enrollment", "lunchStatus", value)} />
                <LabeledInput label="IEP" value={editForm.programParticipation?.iep ? "Yes" : "No"} onChange={(value) => updateNestedSection("programParticipation", "iep", value.toLowerCase() === "yes")} />
                <LabeledInput label="504" value={editForm.programParticipation?.section504 ? "Yes" : "No"} onChange={(value) => updateNestedSection("programParticipation", "section504", value.toLowerCase() === "yes")} />
                <LabeledInput label="ELL" value={editForm.programParticipation?.ell ? "Yes" : "No"} onChange={(value) => updateNestedSection("programParticipation", "ell", value.toLowerCase() === "yes")} />
                <LabeledTextarea label="Staff Notes" value={editForm.customFields?.staffNotes || ""} onChange={(value) => updateNestedSection("customFields", "staffNotes", value)} rows={4} />
              </div>
            </SectionCard>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.rosterHero}>
        <div>
          <div style={styles.title}>Students</div>
          <div style={styles.subtitle}>
            Search the master student index, track current enrollment, review tuition status, and open a complete student profile.
          </div>
        </div>

        <div style={styles.summaryStats}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{totalStudents}</div>
            <div style={styles.statLabel}>Total</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{activeStudents}</div>
            <div style={styles.statLabel}>Active</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{currentlyEnrolled}</div>
            <div style={styles.statLabel}>Current</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{pastDueStudents}</div>
            <div style={styles.statLabel}>Past Due</div>
          </div>
        </div>
      </div>

      <ActionToast message={error} tone="error" onDismiss={() => setError("")} />
      <ActionToast message={!error ? notice : ""} tone="success" onDismiss={() => setNotice("")} />

      <div style={styles.rosterShell}>
        <aside style={styles.rosterSidebar}>
          <div style={styles.sidebarActionRow}>
            <button
              type="button"
              style={styles.sidebarAddButton}
              onClick={handleAddStudent}
              disabled={saving}
            >
              <Plus size={16} />
              {saving ? "Adding Student..." : "Add Student"}
            </button>
          </div>

          <label style={styles.searchBar}>
            <Search size={18} color="var(--color-primary)" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name, student number, state ID, grade, school, teacher, or tag"
              style={styles.searchInput}
            />
          </label>

          <div style={styles.filterRow}>
            <button
              type="button"
              style={statusFilter === "all" ? styles.filterButtonActive : styles.filterButton}
              onClick={() => setStatusFilter("all")}
            >
              All Statuses
            </button>
            <button
              type="button"
              style={statusFilter === "current" ? styles.filterButtonActive : styles.filterButton}
              onClick={() => setStatusFilter("current")}
            >
              Current
            </button>
            <button
              type="button"
              style={statusFilter === "active" ? styles.filterButtonActive : styles.filterButton}
              onClick={() => setStatusFilter("active")}
            >
              Active
            </button>
            <button
              type="button"
              style={tuitionFilter === "past-due" ? styles.filterButtonActive : styles.filterButton}
              onClick={() => setTuitionFilter("past-due")}
            >
              Past Due
            </button>
            <button
              type="button"
              style={tuitionFilter === "all" ? styles.filterButtonActive : styles.filterButton}
              onClick={() => setTuitionFilter("all")}
            >
              Any Tuition
            </button>
          </div>

          <div style={styles.resultsSummary}>
            {filteredStudents.length} student{filteredStudents.length === 1 ? "" : "s"} found
          </div>

          <div style={styles.rosterList}>
            {filteredStudents.length === 0 ? (
              <div style={styles.emptyCard}>
                <div style={styles.emptyTitle}>No students found</div>
              <div style={styles.emptyText}>
                  Add a student here, or try a different search term.
                </div>
                <button
                  type="button"
                  style={styles.emptyActionButton}
                  onClick={handleAddStudent}
                  disabled={saving}
                >
                  <Plus size={16} />
                  {saving ? "Adding Student..." : "Add Student"}
                </button>
              </div>
            ) : (
              filteredStudents.map((student) => (
                <button
                  key={student.id}
                  type="button"
                  style={
                    (previewStudent?.id || "") === student.id
                      ? styles.rosterRowActive
                      : styles.rosterRow
                  }
                  onClick={() => setPreviewStudentId(student.id)}
                >
                  <img src={student.photoUrl} alt={student.displayName} style={styles.rosterThumb} />
                  <div style={styles.rosterBody}>
                    <div style={styles.rosterName}>{student.displayName}</div>
                    <div style={styles.rosterMeta}>
                      {student.studentNumber || "No ID"} • Grade {student.gradeLevel || "?"}
                    </div>
                    <div style={styles.rosterMeta}>
                      {student.currentEnrollmentStatus || student.enrollmentStatus || "Unknown status"}
                    </div>
                    <div style={styles.rosterChips}>
                      <span style={styles.cardPill}>{student.homeroomTeacher || "No teacher selected"}</span>
                      {student.tuitionPaymentStatus ? (
                        <span style={styles.alertPill}>{student.tuitionPaymentStatus}</span>
                      ) : null}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </aside>

        <section style={styles.selectionPanel}>
          {previewStudent ? (
            <div style={styles.previewPanel}>
              <div style={styles.previewHero}>
                <img
                  src={previewStudent.photoUrl}
                  alt={previewStudent.displayName}
                  style={styles.previewPhoto}
                />
                <div style={styles.previewHeroBody}>
                  <div style={styles.previewKicker}>
                    {previewStudent.gradeLevel ? `Grade ${previewStudent.gradeLevel}` : "Student"}
                  </div>
                  <div style={styles.previewName}>{previewStudent.displayName}</div>
                  <div style={styles.previewMeta}>
                    {previewStudent.studentNumber || "No student number"} •{" "}
                    {previewStudent.schoolName || "No school assigned"}
                  </div>
                </div>
              </div>

              <div style={styles.previewStats}>
                <div style={styles.previewStatCard}>
                  <div style={styles.previewStatLabel}>Current Status</div>
                  <div style={styles.previewStatValue}>
                    {previewStudent.currentEnrollmentStatus || previewStudent.enrollmentStatus || "Not set"}
                  </div>
                </div>
                <div style={styles.previewStatCard}>
                  <div style={styles.previewStatLabel}>Tuition</div>
                  <div style={styles.previewStatValue}>
                    {previewStudent.tuitionPaymentStatus || "Not set"}
                  </div>
                </div>
                <div style={styles.previewStatCard}>
                  <div style={styles.previewStatLabel}>Teacher</div>
                  <div style={styles.previewStatValue}>
                    {previewStudent.homeroomTeacher || "Not assigned"}
                  </div>
                </div>
                <div style={styles.previewStatCard}>
                  <div style={styles.previewStatLabel}>Family</div>
                  <div style={styles.previewStatValue}>
                    {previewStudent.householdName || "Not set"}
                  </div>
                </div>
              </div>

              <div style={styles.previewSection}>
                <div style={styles.previewSectionTitle}>Quick View</div>
                <div style={styles.previewSectionText}>
                  {previewStudent.primaryEmail || "No email"} {previewStudent.primaryPhone ? `• ${previewStudent.primaryPhone}` : ""}
                </div>
                <div style={styles.previewSectionText}>
                  {[previewStudent.city, previewStudent.state].filter(Boolean).join(", ") || "No city/state set"}
                </div>
                <div style={styles.previewTagRow}>
                  {(previewStudent.tags || []).length ? (
                    previewStudent.tags.map((tag) => (
                      <span key={tag} style={styles.previewTag}>
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span style={styles.previewTagMuted}>No tags yet</span>
                  )}
                </div>
              </div>

              <div style={styles.previewSection}>
                <div style={styles.previewSectionTitle}>Parents / Guardians</div>
                {(previewStudent.guardians || []).length ? (
                  <div style={styles.previewParentList}>
                    {(previewStudent.guardians || []).slice(0, 3).map((guardian, index) => (
                      <div key={`${guardian.name || "guardian"}-${index}`} style={styles.previewParentCard}>
                        <div style={styles.previewParentHeader}>
                          <div>
                            <div style={styles.previewParentName}>
                              {guardian.name || "Unnamed Guardian"}
                            </div>
                            <div style={styles.previewParentMeta}>
                              {guardian.relationship || "Relationship not set"}
                            </div>
                          </div>
                          <span style={guardian.portalAccess ? styles.portalBadgeActive : styles.portalBadgeMuted}>
                            {guardian.portalAccess ? "Portal On" : "Portal Off"}
                          </span>
                        </div>
                        <div style={styles.previewSectionText}>
                          {guardian.phone || "No phone"} {guardian.email ? `• ${guardian.email}` : ""}
                        </div>
                        <div style={styles.previewParentFlags}>
                          <span>{guardian.pickupApproved ? "Pickup approved" : "Pickup not approved"}</span>
                          {guardian.linkedUserId ? <span>Linked account</span> : null}
                        </div>
                      </div>
                    ))}
                    {(previewStudent.guardians || []).length > 3 ? (
                      <div style={styles.previewSectionText}>
                        +{(previewStudent.guardians || []).length - 3} more on the full profile
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div style={styles.previewSectionText}>No parent or guardian information added.</div>
                )}
              </div>

              <div style={styles.previewActions}>
                <button
                  type="button"
                  style={styles.previewPrimaryButton}
                  onClick={() => {
                    setSelectedStudentId(previewStudent.id);
                    setEditMode(false);
                  }}
                >
                  Open Student
                </button>
                <button
                  type="button"
                  style={styles.previewSecondaryButton}
                  onClick={() => {
                    setSelectedStudentId(previewStudent.id);
                    setEditMode(false);
                    setPendingEditStudentId(previewStudent.id);
                  }}
                >
                  Edit Student
                </button>
              </div>
            </div>
          ) : (
            <div style={styles.selectionEmpty}>
              <div style={styles.selectionTitle}>Select a student</div>
              <div style={styles.selectionText}>
                Choose a student on the left and their quick view will appear here.
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    padding: 18,
  },
  rosterHero: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 8,
    color: "#0f172a",
    display: "grid",
    gap: 18,
    gridTemplateColumns: "minmax(0, 1.6fr) minmax(320px, 1fr)",
    padding: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: 900,
  },
  subtitle: {
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.5,
    marginTop: 6,
    maxWidth: 780,
  },
  summaryStats: {
    display: "grid",
    gap: 8,
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  },
  statCard: {
    background: "#ffffff",
    borderLeft: "1px solid rgba(15,23,42,0.12)",
    padding: "4px 12px",
  },
  statValue: {
    color: "#0f172a",
    fontSize: 24,
    fontWeight: 900,
    lineHeight: 1,
  },
  statLabel: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0,
    marginTop: 8,
    textTransform: "uppercase",
  },
  rosterShell: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "minmax(380px, 460px) minmax(0, 1fr)",
  },
  rosterSidebar: {
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    minHeight: 680,
    padding: 14,
  },
  sidebarActionRow: {
    display: "flex",
    justifyContent: "flex-start",
  },
  sidebarAddButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 14,
    fontWeight: 900,
    gap: 8,
    padding: "12px 16px",
  },
  searchBar: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #cfe0db",
    borderRadius: 8,
    display: "flex",
    gap: 12,
    padding: "12px 14px",
  },
  searchInput: {
    background: "transparent",
    border: "none",
    color: "#0f172a",
    flex: 1,
    fontSize: 15,
    outline: "none",
  },
  resultsSummary: {
    color: "var(--color-primary)",
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  filterButton: {
    background: "#f8fafc",
    border: "1px solid #d6e1dd",
    borderRadius: 8,
    color: "#475569",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "8px 12px",
  },
  filterButtonActive: {
    background: "var(--color-primary)",
    border: "1px solid var(--color-primary)",
    borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "8px 12px",
  },
  rosterList: {
    display: "grid",
    gap: 8,
    overflow: "auto",
    paddingRight: 2,
  },
  rosterRow: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    cursor: "pointer",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "56px minmax(0, 1fr)",
    padding: 8,
    textAlign: "left",
  },
  rosterRowActive: {
    alignItems: "center",
    background: "color-mix(in srgb, var(--color-primary) 7%, #ffffff 93%)",
    border: "1px solid color-mix(in srgb, var(--color-primary) 45%, #d8e4e0 55%)",
    borderRadius: 8,
    cursor: "pointer",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "56px minmax(0, 1fr)",
    padding: 8,
    textAlign: "left",
  },
  rosterThumb: {
    aspectRatio: "1 / 1",
    borderRadius: 8,
    objectFit: "cover",
    width: "100%",
  },
  rosterBody: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
  },
  rosterName: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 800,
  },
  stackCardHeader: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    justifyContent: "space-between",
  },
  portalBadgeActive: {
    background: "#dcfce7",
    border: "1px solid #86efac",
    borderRadius: 8,
    color: "#166534",
    fontSize: 11,
    fontWeight: 800,
    padding: "6px 10px",
    textTransform: "uppercase",
  },
  portalBadgeMuted: {
    background: "#f8fafc",
    border: "1px solid #d6e1dd",
    borderRadius: 8,
    color: "#64748b",
    fontSize: 11,
    fontWeight: 800,
    padding: "6px 10px",
    textTransform: "uppercase",
  },
  stackHint: {
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.5,
  },
  guardianAccessRow: {
    alignItems: "flex-start",
    background: "#f8fafc",
    border: "1px solid #dbe7e3",
    borderRadius: 8,
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    justifyContent: "space-between",
    marginTop: 12,
    padding: 12,
  },
  guardianLinkPanel: {
    background: "#f8fafc",
    border: "1px solid #dbe7e3",
    borderRadius: 8,
    display: "grid",
    gap: 12,
    padding: 14,
  },
  guardianLinkMeta: {
    display: "grid",
    gap: 6,
  },
  guardianLinkControls: {
    alignItems: "end",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "minmax(0, 1fr) auto",
  },
  guardianAccessMeta: {
    display: "flex",
    flex: "1 1 220px",
    flexDirection: "column",
    gap: 6,
    minWidth: 0,
  },
  guardianAccessTitle: {
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 800,
  },
  guardianAccessCopy: {
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.6,
  },
  guardianAccessActions: {
    display: "flex",
    flex: "0 1 auto",
    flexWrap: "wrap",
    gap: 8,
  },
  inlinePrimaryButton: {
    background: "var(--color-primary)",
    border: "1px solid var(--color-primary)",
    borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "10px 12px",
  },
  inlineSecondaryButton: {
    background: "#ffffff",
    border: "1px solid #cfe0db",
    borderRadius: 8,
    color: "#0f172a",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "10px 12px",
  },
  inlineGhostButton: {
    background: "#fff7ed",
    border: "1px solid #fdba74",
    borderRadius: 8,
    color: "#9a3412",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "10px 12px",
  },
  rosterMeta: {
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.4,
  },
  rosterChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 6,
  },
  selectionPanel: {
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 8,
    display: "flex",
    minHeight: 680,
    padding: 18,
  },
  previewPanel: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: 20,
  },
  previewHero: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    color: "#0f172a",
    display: "grid",
    gap: 16,
    gridTemplateColumns: "112px minmax(0, 1fr)",
    padding: 18,
  },
  previewPhoto: {
    aspectRatio: "1 / 1",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    objectFit: "cover",
    width: "100%",
  },
  previewHeroBody: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 0,
  },
  previewKicker: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  previewName: {
    fontSize: 26,
    fontWeight: 900,
    lineHeight: 1.05,
  },
  previewMeta: {
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.5,
  },
  previewStats: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  previewStatCard: {
    background: "#ffffff",
    border: "1px solid color-mix(in srgb, var(--color-primary) 12%, #d8e4e0 88%)",
    borderRadius: 8,
    padding: 16,
  },
  previewStatLabel: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  previewStatValue: {
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1.4,
    marginTop: 8,
  },
  previewSection: {
    background: "#ffffff",
    border: "1px solid color-mix(in srgb, var(--color-primary) 12%, #d8e4e0 88%)",
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: 18,
  },
  previewSectionTitle: {
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 900,
  },
  previewSectionText: {
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.6,
  },
  previewParentList: {
    display: "grid",
    gap: 10,
  },
  previewParentCard: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    display: "grid",
    gap: 8,
    padding: 12,
  },
  previewParentHeader: {
    alignItems: "flex-start",
    display: "flex",
    gap: 10,
    justifyContent: "space-between",
  },
  previewParentName: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 900,
    lineHeight: 1.25,
  },
  previewParentMeta: {
    color: "var(--color-primary)",
    fontSize: 12,
    fontWeight: 800,
    marginTop: 3,
  },
  previewParentFlags: {
    color: "#64748b",
    display: "flex",
    flexWrap: "wrap",
    fontSize: 12,
    fontWeight: 800,
    gap: 8,
  },
  previewTagRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 4,
  },
  previewTag: {
    background: "color-mix(in srgb, var(--color-primary) 12%, #ffffff 88%)",
    borderRadius: 8,
    color: "var(--color-primary)",
    fontSize: 12,
    fontWeight: 800,
    padding: "7px 10px",
  },
  previewTagMuted: {
    color: "#94a3b8",
    fontSize: 13,
    fontWeight: 700,
  },
  previewActions: {
    display: "flex",
    gap: 12,
    justifyContent: "flex-end",
    marginTop: "auto",
  },
  previewPrimaryButton: {
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 800,
    padding: "12px 14px",
  },
  previewSecondaryButton: {
    background: "#ffffff",
    border: "1px solid color-mix(in srgb, var(--color-primary) 25%, #d8e4e0 75%)",
    borderRadius: 8,
    color: "var(--color-primary)",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 800,
    padding: "12px 14px",
  },
  selectionEmpty: {
    maxWidth: 520,
    textAlign: "center",
  },
  selectionTitle: {
    color: "#0f172a",
    fontSize: 28,
    fontWeight: 900,
  },
  selectionText: {
    color: "#64748b",
    fontSize: 15,
    lineHeight: 1.7,
    marginTop: 12,
  },
  emptyActionButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 800,
    gap: 8,
    marginTop: 12,
    padding: "12px 14px",
  },
  cardPill: {
    background: "#ecfdf5",
    borderRadius: 8,
    color: "var(--color-primary)",
    fontSize: 12,
    fontWeight: 800,
    padding: "7px 10px",
  },
  alertPill: {
    background: "#fff7ed",
    borderRadius: 8,
    color: "#c2410c",
    fontSize: 12,
    fontWeight: 800,
    padding: "7px 10px",
  },
  detailPage: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    padding: 18,
  },
  detailTopRow: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    justifyContent: "space-between",
  },
  topRightActions: {
    alignItems: "center",
    display: "flex",
    flex: 1,
    flexWrap: "wrap",
    gap: 12,
    justifyContent: "flex-end",
  },
  backButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 800,
    gap: 8,
    padding: "12px 14px",
  },
  inlineSearch: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #cfe0db",
    borderRadius: 8,
    display: "flex",
    flex: 1,
    gap: 12,
    maxWidth: 520,
    padding: "12px 14px",
  },
  editButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid var(--color-primary)",
    borderRadius: 8,
    color: "var(--color-primary)",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 800,
    gap: 8,
    padding: "12px 14px",
  },
  editActionRow: {
    display: "flex",
    gap: 10,
  },
  editingBadge: {
    alignItems: "center",
    background: "color-mix(in srgb, var(--color-primary) 10%, #ffffff 90%)",
    border: "1px solid color-mix(in srgb, var(--color-primary) 24%, #d8e4e0 76%)",
    borderRadius: 8,
    color: "var(--color-primary)",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 900,
    padding: "12px 14px",
  },
  editSaveBar: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid color-mix(in srgb, var(--color-primary) 24%, #d8e4e0 76%)",
    borderRadius: 8,
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
    display: "flex",
    gap: 16,
    justifyContent: "space-between",
    padding: 12,
  },
  editSaveBarText: {
    color: "#0f172a",
    display: "grid",
    gap: 2,
    minWidth: 0,
  },
  editSaveBarKicker: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  cancelButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 800,
    gap: 8,
    padding: "12px 14px",
  },
  saveButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 800,
    gap: 8,
    padding: "12px 14px",
  },
  error: {
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    borderRadius: 8,
    color: "#be123c",
    fontSize: 14,
    lineHeight: 1.6,
    padding: 12,
  },
  notice: {
    background: "#ecfdf5",
    border: "1px solid #a7f3d0",
    borderRadius: 8,
    color: "#166534",
    fontSize: 14,
    lineHeight: 1.6,
    padding: 12,
  },
  studentHero: {
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 8,
    color: "#0f172a",
    display: "grid",
    gap: 16,
    gridTemplateColumns: "156px minmax(0, 1fr)",
    padding: 16,
  },
  photoColumn: {
    alignItems: "stretch",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  studentPhoto: {
    aspectRatio: "1 / 1",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    objectFit: "cover",
    width: "100%",
  },
  photoUploadButton: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    color: "#0f172a",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 800,
    justifyContent: "center",
    minHeight: 42,
    padding: "10px 12px",
  },
  hiddenFileInput: {
    display: "none",
  },
  heroBody: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  heroKicker: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  heroName: {
    fontSize: 32,
    fontWeight: 900,
    lineHeight: 1.05,
    margin: "8px 0 0",
  },
  heroMeta: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
    color: "#475569",
  },
  heroPills: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 16,
  },
  heroPill: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    color: "#334155",
    fontSize: 12,
    fontWeight: 800,
    padding: "8px 12px",
  },
  heroTag: {
    background: "color-mix(in srgb, var(--color-primary) 10%, #ffffff 90%)",
    borderRadius: 8,
    color: "var(--color-primary)",
    fontSize: 12,
    fontWeight: 800,
    padding: "8px 12px",
  },
  sectionGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  },
  editGrid: {
    display: "grid",
    gap: 12,
  },
  sectionCard: {
    background: "#ffffff",
    border: "1px solid #d8e4e0",
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: 14,
  },
  sectionHeader: {
    alignItems: "center",
    display: "flex",
    gap: 10,
    justifyContent: "space-between",
  },
  sectionHeaderLeft: {
    alignItems: "center",
    display: "flex",
    gap: 10,
    minWidth: 0,
  },
  sectionIcon: {
    alignItems: "center",
    background: "color-mix(in srgb, var(--color-primary) 12%, white 88%)",
    borderRadius: 8,
    color: "var(--color-primary)",
    display: "flex",
    height: 36,
    justifyContent: "center",
    width: 36,
  },
  sectionTitle: {
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 800,
  },
  sectionEditButton: {
    alignItems: "center",
    background: "color-mix(in srgb, var(--color-primary) 10%, white 90%)",
    border: "1px solid color-mix(in srgb, var(--color-primary) 26%, #d8e4e0 74%)",
    borderRadius: 8,
    color: "var(--color-primary)",
    cursor: "pointer",
    display: "inline-flex",
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 800,
    gap: 6,
    lineHeight: 1,
    padding: "9px 12px",
    transition: "all 160ms ease",
  },
  sectionBody: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  detailGrid: {
    display: "grid",
    gap: 0,
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  },
  detailCard: {
    background: "#ffffff",
    borderTop: "1px solid #e2e8f0",
    padding: "10px 12px 10px 0",
  },
  detailLabel: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  detailValue: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.4,
    marginTop: 6,
    whiteSpace: "pre-line",
  },
  stackList: {
    display: "grid",
    gap: 12,
  },
  tuitionSubgrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    marginTop: 4,
  },
  tuitionPanel: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    display: "grid",
    gap: 10,
    padding: 14,
  },
  miniRow: {
    alignItems: "center",
    borderTop: "1px solid #e2e8f0",
    color: "#334155",
    display: "flex",
    fontSize: 13,
    gap: 10,
    justifyContent: "space-between",
    lineHeight: 1.4,
    paddingTop: 8,
  },
  stackEditList: {
    display: "grid",
    gap: 14,
  },
  stackCard: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: 12,
  },
  stackEditCard: {
    background: "#f8fafc",
    border: "1px solid #dbe7e3",
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    gap: 14,
    padding: 14,
  },
  stackEditHeader: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
  },
  inlineButtonRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-end",
  },
  stackEditTitle: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 900,
  },
  addMiniButton: {
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 800,
    padding: "12px 14px",
    justifySelf: "start",
  },
  removeMiniButton: {
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    borderRadius: 8,
    color: "#be123c",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "8px 10px",
  },
  stackTitle: {
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 800,
  },
  stackMeta: {
    color: "var(--color-primary)",
    fontSize: 13,
    fontWeight: 700,
    marginTop: 4,
  },
  stackCopy: {
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.5,
    marginTop: 8,
  },
  gradeScore: {
    alignItems: "flex-end",
    color: "#0f172a",
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    fontSize: 15,
    fontWeight: 900,
    gap: 2,
    lineHeight: 1.2,
  },
  reportSnapshotGrid: {
    display: "grid",
    gap: 8,
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    marginTop: 12,
  },
  reportSnapshotItem: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    color: "#334155",
    display: "flex",
    fontSize: 12,
    fontWeight: 800,
    gap: 8,
    justifyContent: "space-between",
    padding: "9px 10px",
  },
  additionalList: {
    display: "grid",
    gap: 10,
  },
  additionalRow: {
    borderTop: "1px solid #e2e8f0",
    display: "grid",
    gap: 8,
    gridTemplateColumns: "minmax(140px, 180px) minmax(0, 1fr)",
    paddingTop: 10,
  },
  additionalLabel: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    textTransform: "capitalize",
  },
  additionalValue: {
    color: "#0f172a",
    fontSize: 13,
    lineHeight: 1.5,
    whiteSpace: "pre-line",
  },
  emptyInline: {
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.5,
  },
  emptyCard: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d8e4e0",
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    justifyContent: "center",
    minHeight: 240,
    padding: 24,
    textAlign: "center",
  },
  emptyTitle: {
    color: "#0f172a",
    fontSize: 20,
    fontWeight: 800,
  },
  emptyText: {
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.6,
    maxWidth: 420,
  },
  formGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  },
  jsonGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  fieldHint: {
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.6,
  },
  fieldLabel: {
    color: "#334155",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  input: {
    background: "#ffffff",
    border: "1px solid #d8e4e0",
    borderRadius: 8,
    color: "#0f172a",
    fontSize: 14,
    outline: "none",
    padding: "12px 14px",
  },
  textarea: {
    background: "#ffffff",
    border: "1px solid #d8e4e0",
    borderRadius: 8,
    color: "#0f172a",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 13,
    lineHeight: 1.5,
    minHeight: 120,
    outline: "none",
    padding: "12px 14px",
    resize: "vertical",
  },
};
